/**
 * THE AGENTS' dictionary — who they are, what they may do, and the chat itself.
 *
 * Same seed and same shape as `features/billing/i18n/*` and `features/inbox`:
 * ENGLISH is the origin, pt-BR is the translation, every key is an English noun,
 * and a key missing from a translation falls back to the source sentence rather
 * than to the identifier.
 *
 * ── THE THREE WORDS THIS SCREEN MUST NEVER BLUR ───────────────────────────
 *
 * 1. An `interaction` agent is the ATENDENTE — the one that answers CUSTOMERS
 *    on the channels. In English it is the ATTENDANT, never "assistant" and
 *    never "bot": `role.attendant` is the only key allowed to name it, and the
 *    inbox says the same word about the same thing.
 * 2. `agent` on its own is the generic — the thing an account has several of,
 *    of any mode. Every sentence that could mean either says which.
 * 3. The BUILDER and BEN are the system's own two, and they work with the TEAM
 *    inside the panel. `sidebar.systemHint` is the line that keeps them from
 *    reading as two more attendants somebody hired.
 *
 * ── WHAT THE TOOL LABELS ARE FOR ──────────────────────────────────────────
 *
 * `tool.*` is a view over names the platform already publishes at
 * `/v3/agents/tools`. An owner watching Ben work cannot audit a turn written in
 * `write_structure`, and a tool missing from this map falls back to its own name
 * with the underscores removed — worse than a label, better than nothing. The
 * verbs are deliberately concrete: "Reading records" says what "Querying data"
 * hides, and the whole value of the line is telling whether the agent is doing
 * what was asked.
 *
 * ── WHAT IS NOT HERE ──────────────────────────────────────────────────────
 *
 * The tenant's own words: an agent's name, its CODE (`atendimento`, the address
 * rules and channels point at and which never changes), a persona somebody
 * wrote, the text of a reply, the raw input and output of a tool call. And the
 * mode names on the wire — `interaction`, `operator`, `builder` — which are
 * contract, shown as `<code>` where they are shown at all.
 */
import { agentToolMessages } from "@/shared/i18n/agent-tools";

export const agentsMessages = {
	en: {
		/** A moldura do editor de UM agente: abas, liga/desliga e o atendente que não conversa aqui. */
		workbench: {
			settingsTab: "Settings",
			knowledgeTab: "Knowledge",
			chatTab: "Conversation",
			testTab: "Test agent",
			on: "on",
			off: "off",
			turnOn: "Turn on",
			turnOff: "Turn off",
			attendant: {
				title: "This one is an attendant: it talks to your customers.",
				why: "It does not answer here in the panel — that is the rule that stops somebody outside from asking to drop a table. To try it, open the chat the way a visitor would.",
				test: "Try it as a customer",
				noChat:
					"You do not have a chat published on the site yet. Create a {webchat} channel under Channels — no verified number and no approved app needed — and publish it. It takes a minute and it is the fastest way to see this agent working.",
				webchat: "webchat",
			},
		},
		resources: {
			heading: "Resources",
			files: "Create a document",
			media: "Create an image",
			web: "Search the web",
			filesRequest: "Create a document about: ",
			mediaRequest: "Create an image of: ",
			webRequest: "Search the web for: ",
		},
		document: {
			saveFailed: "Could not save. Your draft is still here.",
			actions: "Document actions",
			openTab: "Open in new tab",
			openPdf: "Open PDF",
			tableLimit: "Preview limited to 200 rows and 100 columns. Download the complete file.",
			edit: "Edit document",
			save: "Save new version",
			cancel: "Cancel",
			compare: "Compare versions",
			conflict:
				"This file changed while you were editing. Copy your changes, close the editor and reopen the latest version.",

			versions: "Versions",
			version: "Version {version}",
			restore: "Ask to restore",
			revise: "Adjust selection",
			reviseRequest: "In document {name}, adjust this excerpt:\n“{text}”\nRequested change: ",
			restoreRequest: "Restore version {version} of file {name}.",

			heading: "Document",
			close: "Back to conversation",
			preview: "Preview",
			source: "Source",
			writing: "Writing…",
			incomplete: "Incomplete preview",
			loading: "Loading preview…",
			failed: "Could not preview this file. You can still download it.",
			open: "Open preview",
		},
		role: {
			/** The one that answers CUSTOMERS. Never "assistant", never "bot". */
			attendant: "attendant",
		},
		sidebar: {
			search: "Search agent",
			attendants: "Attendants",
			attendantsHint: "They talk to your customers, on the channels.",
			system: "The system",
			systemHint: "Ben works with your team across the workspace.",
			/** Which channels point at THIS attendant. Absent when none does. */
			answers: "Answers: {names}",
			off: "switched off",
			noMatch: "No agent by that name.",
			newAttendant: "+ New attendant",
		},
		settings: {
			outbound: {
				title: "Messages to customers",
				lead: "Applies to everything this agent says to customers — its replies inside a conversation and when {thisAgent}.",
				leadDecides: "this agent decides to write to somebody",
				sendLabel: "Sends on its own",
				sendHint:
					"How it works today. The message goes out at once and appears in the conversation.",
				askLabel: "Waits for approval",
				askHint:
					"The message sits in the conversation as a draft, with Send, Edit and Refuse. Nothing leaves until somebody clicks.",
			},
			name: {
				title: "Name",
				lead: "What it is called. It is the name that appears in the channel picker and on the conversations — its role ({role}) does not change with it.",
				label: "Agent's name",
			},
			tier: {
				title: "Model",
				fastHint: "short answers and simple tasks",
				smartHint: "day-to-day operation",
				premiumHint: "building structure, hard decisions",
				fast: "Fast",
				smart: "Smart",
				premium: "Premium",
			},
			sex: {
				title: "Sex",
				hint: "Picks the voice it speaks with, and the face it is given.",
				male: "Male",
				female: "Female",
				neutral: "Neutral",
				none: "Not said",
			},
			copy: {
				/** The copy's NAME. The copy's CODE is a code and is not translated. */
				nameSuffix: "{name} (copy)",
				lead: "The copy is born {off}: it inherits this agent's persona and tools, and nothing of it reaches anybody until you switch it on.",
				leadOff: "switched off",
				namePlaceholder: "Name of the copy",
				nameLabel: "Name of the copy",
				codeLabel: "Code of the copy",
				action: "Duplicate",
			},
			remove: {
				lead: "Deleting {name} does not erase the history: everything it has answered stays under Usage. What goes is the configuration — persona, tools and the code {code}, which automations and channels may be using.",
				confirm: "Delete anyway",
				action: "Delete",
			},
			discard: "Discard",
		},
		persona: {
			title: "Who it is",
			filled: "{n} of 5 answered",
			missingEscalation: "· it still has to say when to call a person",
			lead: "This is ADDED to the role's instructions — it never replaces them, so nothing here switches off the safety rules. What the company is and what nobody may say it already inherits from the Account; do not repeat it here.",
			objective: {
				label: "What it exists for",
				hint: "Its task, in one sentence. It is what it tries to accomplish in every conversation.",
				placeholder:
					"Resolve questions from people who already bought — order, delivery and exchange — without having to pass it on.",
			},
			tone: {
				label: "How it speaks",
				hint: "The manner, not the content: close or formal, short or detailed.",
				placeholder:
					"Direct and cordial. Short sentences, no jargon. Calls the person by their first name and confirms what it understood before acting.",
			},
			guardrails: {
				label: "What it never does",
				hint: "Applies to THIS agent only. What nobody in the company may say lives under Settings → Workspace → The business (Fixed rules), and already applies here.",
				placeholder:
					"Never promises a delivery date without checking the system. Never offers a discount. Never asks for card details.",
			},
			knowledgeScope: {
				label: "What it talks about",
				hint: "Its subject. Outside it, it never makes something up: it looks it up or asks the team for help.",
				placeholder:
					"Orders, deadlines, exchanges and returns. Does not talk about contracts, about the price of a new product, or about legal matters.",
			},
			escalation: {
				label: "When it calls a person",
				hint: "The sign to stop and ask for help. Without it, it insists on its own in the very case where insisting is the mistake.",
				placeholder:
					"When somebody asks to cancel, when they mention a formal complaint, or when the same question comes back for the third time.",
			},
			freeText: {
				open: "Other instructions",
				closed: "+ Other instructions",
				lead: "Free text, sent after the five fields above. It is where whatever was already written here before them lives.",
				label: "Free persona text",
			},
			preview: "See the text it receives",
		},
		create: {
			titleAttendant: "New attendant",
			titleTeam: "New agent for the team",
			leadAttendant:
				"An attendant answers your customers on the channels — WhatsApp, site, Instagram.",
			leadTeam: "It works in here, in the panel, with you and the team — it appears on no channel.",
			nameLabel: "Name",
			namePlaceholder: "After-sales",
			codeHint:
				"Fixed address: {code} — automations and channels point at it and it does not change afterwards.",
			advanced: "Advanced: create an agent for the team, not an attendant",
			hideAdvanced: "Hide — I only want an attendant",
			whoItTalksTo: "Who it talks to",
			modeInteraction: "With your customers",
			modeInteractionHint:
				"Answers on the channels. This is the one that replies on WhatsApp, on the site, on Instagram.",
			modeOperator: "With your team",
			modeOperatorHint:
				"Works with your team: operates, builds screens and automates with your permissions.",
			objectiveLabel: "What it exists for",
			objectiveHint:
				"The tone, the limits and when to call a person you adjust afterwards, under Adjust this agent.",
		},
		/**
		 * O QUE UM AGENTE SABE, em três títulos — lido de
		 * `/v3/agents/:code/capabilities`, pelas mesmas funções que o turno usa,
		 * para o painel nunca prometer o que o turno não oferece.
		 *
		 * `artifacts` é SABE FAZER: as funções do sistema ao alcance dele, com um
		 * interruptor por agente. `sees` é SABE SOBRE: o que lhe põem na mão antes
		 * de responder. `tools` é SEMPRE SABE: as mãos que vêm com o modo, que
		 * ninguém liga aqui — `ToolMatrix`, logo abaixo, é que é o editor.
		 */
		capabilities: {
			title: "What {name} knows",
			sees: {
				title: "Knows about",
				lead: "What is put in its hands before it answers.",
				businessProfile: "business profile",
				brandRules: "brand rules",
				persona: "persona",
				memories: "{n} memory | {n} memories",
				documents: "{n} document | {n} documents",
				fixBusiness: "fill it in under Account",
				fixBrand: "write them under Account",
				fixPersona: "answer it below",
				fixDocuments: "upload under Memory",
			},
			artifacts: {
				title: "Knows how to do",
				lead: "Your system's own functions. With the switch on, {name} can use the function on its own.",
				empty: "no function is on air yet",
				inputs: "Takes: {list}",
				optional: "{name} (optional)",
				byMode: "because it is {mode}",
				byAutomation: "runs by automation",
				teamOnly: "team only",
				switchLabel: "let {name} run {artifact}",
				error: "Could not change who may run this.",
			},
			tools: {
				title: "Always knows",
				lead: "What it does on its own, in every conversation.",
				count: "{n} tool | {n} tools",
			},
			error: "Could not read what this agent knows.",
		},
		tools: {
			title: "Tools",
			count: "({on} of {total})",
			lead: "Only the tools the {mode} mode can see. Switching one off is the same as it not existing for this agent.",
			allOn: "All switched on — nothing has been restricted yet.",
			technical: "Technical details",
		},
		authority: {
			title: "Authority",
			hint: "An agent has a role, like a person. Access never goes above the role. What it sees — tables, channels, funnels, screens — is set in Settings → Access, for people and agents alike.",
			role: "Runs as {role}",
			changeIn: "Change under Settings → Access",
		},
		chat: {
			composePlaceholder: "Write a message…",
			placeholder: "Write…  Enter sends, Shift+Enter breaks the line",
			answering: "answering…",
			stop: "Stop",
			/** O par da V2 durante o turno: parar o que corre, ou mandar a próxima junto. */
			queue: "Queue",
			queued: "Next: {text}",
			attach: "Attach a file",
			attachHint:
				"Attach a file (image, video, audio, PDF, sheet) — or just paste (Ctrl+V) and drag",
			record: "Record a voice note",
			stopRecording: "Finish recording",
			cancelRecording: "Discard recording",
			voiceNote: "🎙️ Voice note",
			removeAudio: "Remove the voice note",
			noMicrophone: "Could not reach the microphone.",
			removeAttached: "Remove {name}",
			sentImage: "attached image",
			editQuestion: "Correct the question",
			cancelEdit: "cancel",
			resend: "resend",
			editAndResend: "edit and resend",
			silent: "The agent chose not to answer this time.",
			fromAgent: "Result from a background agent",
			refused:
				"The model provider declined this message. It stays out of this conversation's context — ask again in other words.",
			longThread:
				"Long conversation — every reply rereads all of it. Starting a new one is cheaper and faster.",
			toEnd: "↓ go to the end",
			showMore: "Show more",
			showLess: "Show less",
			copy: "copy",
			copied: "copied",
			useAsReply: "use as a reply",
			regenerate: "regenerate",
			deleteMessage: "delete message",
			confirmDelete: "delete for real?",
			tokens: "· {n} tokens",
			/** The message when a picture arrives with no words of its own. */
			whatIsThis: "What is this?",
		},
		suggestions: {
			/** A saudação da conversa vazia — o rosto e a pergunta, como na V2. */
			hello: "Hi 👋",
			toStart: "To start",
			orWrite: "Tap a suggestion, or write what you need.",
			orWriteAlone: "Write a question, a request or a command to start.",
			manage: {
				title: "Suggested questions",
				lead: "What the assistant offers when it is opened on each screen. Written from your business, and yours to edit.",
				generate: "Write them from my business",
				generating: "writing…",
				generated: "{n} questions written.",
				unavailable: "No model could be reached. Nothing was changed.",
				unreadable: "The answer could not be read. Nothing was changed.",
				failed: "Could not write them: {reason}. Nothing was changed.",
				anyScreen: "Any screen",
				gone: "A screen no longer in the menu",
				add: "New question",
				questionLabel: "The question, in the words the person would use",
				shortLabel: "Short text for the button (optional)",
				save: "Save",
				cancel: "Cancel",
				remove: "Remove",
				turnOn: "turn on",
				turnOff: "turn off",
				off: "off",
				byModel: "written by the model",
				empty: "Nothing written yet. Ask for them from your business, or write the first one.",
				/** As de fábrica — ver o bloco pt-BR para o porquê do verbo. */
				factoryHint:
					"These are the factory ones: what Ben offers on every screen while this workspace has written none.",
				adopt: "Keep a copy I can edit",
				screenPick: "Screen",
			},
			hintInConversation:
				"Ask what to answer. It reads the workspace's memory and does not talk to the customer.",
			hintInternal: "Ask Ben to operate, build screens and automate your work.",
			panel: {
				/**
				 * A PRIMEIRA das três, por decisão do dono (24/09): o que um workspace
				 * recém-nascido quer do Ben é o site no ar, não um resumo do dia que
				 * ainda não teve nada dentro.
				 */
				site: "Create my business website",
				/**
				 * UM GESTO NATIVO (desde 05/10 o atendente etiqueta a pessoa da
				 * conversa sem pacote nenhum, e o Ben do painel sempre etiquetou).
				 *
				 * Escrita da boca de QUEM PEDE, como todas as outras: o que se toca
				 * aqui é um pedido ao Ben, não uma frase dele.
				 */
				tagging: "Tag the conversations for me",
				/** Os atalhos do "/" no compositor: a tela com papel `composer.snippet`. */
				snippets: "Set up the quick replies for support",
				screen: "Create a quotes screen",
				/**
				 * A pergunta de quem JÁ TEM MOVIMENTO — e a última da oferta, por
				 * isso: numa casa recém-aberta não há registro para estar parado.
				 */
				stalled: "Which records have been stalled for more than a week?",
				/**
				 * NÃO está na oferta, e é usada como CONTRA-EXEMPLO: ela é a pergunta
				 * certa no painel e a errada com um cliente à espera (`ben-dock.spec`).
				 */
				today: "What changed today?",
			},
			attendant: {
				discount: "How would you answer a request for a discount?",
			},
			inConversation: {
				reply: "Suggest a reply",
				summarise: "Summarise this conversation in three lines",
				known: "What do we already know about this customer?",
			},
		},
		missionLive: {
			main: "Ben",
			thinking: "Thinking",
			reading: "Reading {site}",
			readingPage: "Reading a page",
			/** `change_system` numa missão é sempre um PLANO: ninguém ao volante aplica (QA-007). */
			drafting: "Drafting the proposal",
			crew: {
				title: "{n} parts of the proposal being drafted at the same time",
				done: "{n} ready",
				running: "{n} in progress",
				queued: "{n} waiting",
				chars: "{kb} KB written",
				planning: "planning",
			},
			status: { queued: "queued", running: "writing", succeeded: "done", failed: "failed" },
		},
		live: {
			acting: "acting…",
			writing: "writing {tool} — {kb} KB…",
			writingLive: "live",
			activity: "Activity",
			reasoningRaw: "reasoning (dev)",
			tasks: "{n} steps, {running} running",
			credits: "{n} cr",
			expand: "expand",
			collapse: "collapse",
			awaitsHuman: "Waiting for a person to answer.",
			running: "in progress",
			thinking: "thinking…",
			reasoning: "reasoning",
			closeOutput: "close",
			seeOutput: "see the output",
		},
		steps: {
			one: "tool",
			many: "tools",
			input: "input",
			output: "output",
			/** What the RETORNO box says while the tool has not answered yet. */
			running: "running…",
			/** A nested object in a tool's arguments, counted rather than spelled out. */
			fields: "{n} fields",
		},
		approval: {
			approve: "Approve",
			done: "Approved — the agents already use it.",
			discarded: "This proposal was discarded.",
			failed: "Could not approve. Try again or use Memory → What the team taught.",
			replaces: "replaces the current line",
			toLibrary: "goes to the Library",
			forAgent: "only for {agent}",
			title: {
				generic: "Waiting for your approval",
				knowledge: "Knowledge for the agents",
			},
		},
		navigate: {
			contact: "contact",
			conversation: "conversation",
			record: "record",
			screen: "screen",
			back: "back",
			open: "open",
			toolFile: "file returned by the tool",
		},
		sessions: {
			searchFailed: "Search failed. Try again.",
			filter: "Filter conversations",
			empty: "No conversation yet.",
			noMatch: "Nothing matching “{term}”.",
			aboutConversation: "About a conversation",
			conversationBadge: "conversation",
			untitled: "Untitled",
			messages: "{n} messages",
			removeLabel: "Delete {title}",
			thisConversation: "this conversation",
			confirmRemove: "confirm",
			remove: "delete",
			/** O cabeçalho do painel de conversas quando ele ocupa a doca inteira. */
			heading: "Previous conversations",
			/** Os grupos por data, no fuso do workspace. */
			groups: {
				today: "Today",
				yesterday: "Yesterday",
				week: "Last 7 days",
				older: "Older",
			},
			ago: {
				now: "now",
				minutes: "{n} min",
				hours: "{n} h",
				days: "{n} d",
			},
		},
		dock: {
			/**
			 * A PROMESSA da coluna da conversa, e ela vem antes de tudo.
			 *
			 * Todo o risco de uma conversa desenhada ao lado de outra é alguém
			 * acreditar que está a falar com o cliente. O servidor já tira o envio
			 * do menu nesta porta; esta linha é a mesma verdade dita na moldura.
			 */
			purposeInConversation: "only you see this — nothing here is sent",
			/** Recomeçar aqui ARQUIVA a thread desta conversa: pede o segundo clique. */
			confirmRestart: "Start over? Click again",
			noBen:
				"This workspace has no panel assistant switched on. Switch one on under Agents to ask for help in here.",
			purposeInternal: "operate, build and automate",
			acting: "using a tool…",
			typing: "writing…",
			thinking: "thinking…",
			whichAgent: "Which agent to talk to",
			conversations: "Conversations",
			newConversation: "New conversation",
			/** O modo de vista: a gaveta na lateral, ou a janela ocupando a tela. */
			wide: "full screen",
			narrow: "back to the side",
			close: "Close",
			/** O botão de histórico com o painel aberto: o clique seguinte devolve o chat. */
			backToChat: "Back to the conversation",
			hideHistory: "Hide conversations",
			copilot: "Ben · Copilot",
			launcher: "Chat with {name}",
			resize: "Drag to resize the conversation",
			tipTitle: "{name} is here",
			tipBody:
				"Tap or click the highlighted orb in the top bar to open Ben. Click again to close the chat. Your conversation stays here.",
			tipShortcut:
				"On a keyboard: Ctrl + J (Windows/Linux) or ⌘ + J (Mac) opens or closes Ben when you’re not typing in a field.",
			tipDone: "That's it! Whenever you need help, just click.",
			tipDismiss: "Got it",
		},
		attach: {
			/** A file with no name of its own, and the word for its unknown type. */
			file: "file",
			openFile: "Open {name}",
			open: "open",
			download: "download",
			noPort: "This link does not open files.",
			downloadFailed: "Could not download that file.",
			openFailed: "Could not open that file.",
			badType: "{name}: Ben reads {formats}.",
			tooBig: "{name}: file too large ({max} MB maximum).",
			uploadFailed: "{name}: the upload failed — try again.",
			tooMany: "Up to {max} files per message.",
			uploading: "uploading…",
			unreadable: "could not read the image",
		},
		rating: {
			up: "the answer helped",
			down: "the answer did not help",
			seeTurn: "see the turn",
			whatWasWrong: "what was wrong? (optional)",
			whatWasWrongLabel: "What was wrong",
			send: "send",
		},
		recovery: {
			checking: "The connection dropped — checking whether the answer arrived…",
			arrived: "The connection dropped mid-way, but the answer arrived.",
			lost: "The connection dropped. The answer may have been produced and archived anyway — look at the turn before sending it again.",
			seeTurn: "see the turn",
		},
		error: {
			expired: "your session expired — sign in again",
			couldNotStart: "could not start ({status})",
			somethingWentWrong: "something went wrong",
			forbidden: "Only an administrator can do that.",
			unreachable: "Could not reach the server.",
			generic: "Could not finish it.",
		},
		...agentToolMessages.en,
	},
	"pt-BR": {
		workbench: {
			settingsTab: "Ajustes",
			knowledgeTab: "Conhecimento",
			chatTab: "Conversar",
			testTab: "Testar atendente",
			on: "ligado",
			off: "desligado",
			turnOn: "Ligar",
			turnOff: "Desligar",
			attendant: {
				title: "Atendente: fala com seus clientes.",
				why: "Não responde aqui no painel — essa é a regra que impede alguém de fora pedir para apagar uma tabela. Para experimentar, abra o chat como um visitante abriria.",
				test: "Testar como cliente",
				noChat:
					"Você ainda não tem um chat no site publicado. Crie um canal {webchat} em Canais — não precisa de número verificado nem app aprovado — e publique. Leva um minuto e é a forma mais rápida de ver o atendimento funcionando.",
				webchat: "webchat",
			},
		},
		resources: {
			heading: "Recursos",
			files: "Criar documento",
			media: "Criar imagem",
			web: "Pesquisar na web",
			filesRequest: "Crie um documento sobre: ",
			mediaRequest: "Crie uma imagem de: ",
			webRequest: "Pesquise na web sobre: ",
		},
		document: {
			saveFailed: "Não foi possível salvar. Seu rascunho continua aqui.",
			actions: "Ações do documento",
			openTab: "Abrir em nova aba",
			openPdf: "Abrir PDF",
			tableLimit: "Prévia limitada a 200 linhas e 100 colunas. Baixe o arquivo completo.",
			edit: "Editar documento",
			save: "Salvar nova versão",
			cancel: "Cancelar",
			compare: "Comparar versões",
			conflict:
				"Este arquivo mudou enquanto você editava. Copie suas alterações, feche o editor e reabra a versão mais recente.",

			versions: "Versões",
			version: "Versão {version}",
			restore: "Pedir restauração",
			revise: "Ajustar seleção",
			reviseRequest: "No documento {name}, ajuste este trecho:\n“{text}”\nAlteração desejada: ",
			restoreRequest: "Restaure a versão {version} do arquivo {name}.",

			heading: "Documento",
			close: "Voltar à conversa",
			preview: "Prévia",
			source: "Código / fonte",
			writing: "Escrevendo…",
			incomplete: "Prévia incompleta",
			loading: "Carregando prévia…",
			failed: "Não foi possível visualizar este arquivo. Você ainda pode baixá-lo.",
			open: "Abrir prévia",
		},
		role: {
			attendant: "atendente",
		},
		sidebar: {
			search: "Buscar pelo nome",
			attendants: "Atendentes",
			attendantsHint: "Falam com seus clientes, nos canais.",
			system: "O sistema",
			systemHint: "O Ben trabalha com sua equipe em todo o sistema.",
			answers: "Responde: {names}",
			off: "desligado",
			noMatch: "Ninguém com esse nome.",
			newAttendant: "+ Novo atendente",
		},
		settings: {
			outbound: {
				title: "Mensagens para clientes",
				lead: "Vale para tudo o que é dito a clientes — as respostas dentro da conversa e quando {thisAgent}.",
				leadDecides: "a decisão é escrever para alguém",
				sendLabel: "Envia sozinho",
				sendHint: "Como funciona hoje. A mensagem sai na hora e aparece na conversa.",
				askLabel: "Espera alguém aprovar",
				askHint:
					"A mensagem fica na conversa como rascunho, com Enviar, Editar e Recusar. Nada sai até alguém clicar.",
			},
			name: {
				title: "Nome",
				lead: "O nome que aparece no seletor de canal e nas conversas — o papel ({role}) não muda com isso.",
				label: "Nome",
			},
			tier: {
				title: "Modelo",
				fastHint: "respostas curtas e tarefas simples",
				smartHint: "operação do dia a dia",
				premiumHint: "montar estrutura, decisões difíceis",
				fast: "Rápido",
				smart: "Inteligente",
				premium: "Avançado",
			},
			sex: {
				title: "Sexo",
				hint: "Escolhe a voz com que fala e o rosto que recebe.",
				male: "Masculino",
				female: "Feminino",
				neutral: "Neutro",
				none: "Não dito",
			},
			copy: {
				nameSuffix: "{name} (cópia)",
				lead: "A cópia nasce {off}: ela herda o jeito de atender e as ferramentas do original, e nada dela sai para ninguém até você ligá-la.",
				leadOff: "desligada",
				namePlaceholder: "Nome da cópia",
				nameLabel: "Nome da cópia",
				codeLabel: "Código da cópia",
				action: "Duplicar",
			},
			remove: {
				lead: "Excluir {name} não apaga o histórico: tudo o que já foi respondido continua em Uso. O que some é a configuração — jeito de atender, ferramentas e o código {code}, que automações e canais podem estar usando.",
				confirm: "Excluir mesmo assim",
				action: "Excluir",
			},
			discard: "Descartar",
		},
		persona: {
			title: "Quem é, e como atende",
			filled: "{n} de 5 preenchidas",
			missingEscalation: "· falta dizer quando chamar uma pessoa",
			lead: "Isto é somado às instruções que vêm com o papel — nunca as substitui, então nada aqui desliga as regras de segurança. O que a empresa é e o que ninguém pode dizer já vem da Conta; não repita aqui.",
			objective: {
				label: "Para que existe",
				hint: "A tarefa, em uma frase. É o que tenta cumprir em toda conversa.",
				placeholder:
					"Resolver dúvida de quem já comprou — pedido, entrega e troca — sem precisar passar para alguém.",
			},
			tone: {
				label: "Como fala",
				hint: "O jeito, não o conteúdo: próximo ou formal, curto ou detalhado.",
				placeholder:
					"Direto e cordial. Frases curtas, sem jargão. Chama a pessoa pelo primeiro nome e confirma o que entendeu antes de agir.",
			},
			guardrails: {
				label: "O que nunca faz",
				hint: "Vale só aqui. O que ninguém da empresa pode dizer fica em Configurações → Workspace → O negócio (Regras fixas), e já se aplica aqui.",
				placeholder:
					"Nunca promete data de entrega sem conferir no sistema. Nunca oferece desconto. Nunca pede dados de cartão.",
			},
			knowledgeScope: {
				label: "Sobre o que fala",
				hint: "O assunto. Fora disso não inventa: confere no sistema ou pede ajuda à equipe.",
				placeholder:
					"Pedidos, prazos, trocas e devoluções. Não fala de contrato, de preço de produto novo nem de assunto jurídico.",
			},
			escalation: {
				label: "Quando chamar uma pessoa",
				hint: "O sinal de parar e pedir ajuda. Sem isto, insiste sem ajuda no caso em que insistir é o erro.",
				placeholder:
					"Quando pedem cancelamento, quando falam em reclamação formal, ou quando a mesma pergunta volta pela terceira vez.",
			},
			freeText: {
				open: "Outras instruções",
				closed: "+ Outras instruções",
				lead: "Texto livre, enviado depois dos cinco campos acima. É onde fica o que já estava escrito aqui antes deles.",
				label: "Outras instruções, em texto livre",
			},
			preview: "Ver o texto completo das instruções",
		},
		create: {
			titleAttendant: "Novo atendente",
			titleTeam: "Novo agente para a equipe",
			leadAttendant: "Um atendente responde seus clientes nos canais — WhatsApp, site, Instagram.",
			leadTeam:
				"Trabalha aqui dentro do painel, com você e a equipe — não aparece em canal nenhum.",
			nameLabel: "Nome",
			namePlaceholder: "Pós-venda",
			codeHint:
				"Endereço fixo: {code} — automações e canais apontam para ele e ele não muda depois.",
			advanced: "Avançado: criar um agente para a equipe, não um atendente",
			hideAdvanced: "Esconder — quero só um atendente",
			whoItTalksTo: "Com quem fala",
			modeInteraction: "Com seus clientes",
			modeInteractionHint:
				"Atende nos canais. É este que responde no WhatsApp, no site, no Instagram.",
			modeOperator: "Com a sua equipe",
			modeOperatorHint:
				"Trabalha com sua equipe: opera, cria telas e automatiza com suas permissões.",
			objectiveLabel: "Para que existe",
			objectiveHint:
				"O tom, os limites e quando chamar uma pessoa você ajusta depois, nos Ajustes.",
		},
		/**
		 * O QUE UM AGENTE SABE, em três títulos — lido de
		 * `/v3/agents/:code/capabilities`, pelas mesmas funções que o turno usa,
		 * para o painel nunca prometer o que o turno não oferece.
		 *
		 * `artifacts` é SABE FAZER: as funções do sistema ao alcance dele, com um
		 * interruptor por agente. `sees` é SABE SOBRE: o que lhe põem na mão antes
		 * de responder. `tools` é SEMPRE SABE: as mãos que vêm com o modo, que
		 * ninguém liga aqui — `ToolMatrix`, logo abaixo, é que é o editor.
		 */
		capabilities: {
			title: "O que {name} sabe",
			sees: {
				title: "Sabe sobre",
				lead: "O que já recebe pronto antes de responder.",
				businessProfile: "ficha do negócio",
				brandRules: "regras de marca",
				persona: "jeito de atender",
				memories: "{n} memória | {n} memórias",
				documents: "{n} documento | {n} documentos",
				fixBusiness: "preencher em Conta",
				fixBrand: "escrever em Conta",
				fixPersona: "responder abaixo",
				fixDocuments: "enviar em Memória",
			},
			artifacts: {
				title: "Sabe fazer",
				lead: "As funções do seu sistema. Com a chave ligada, {name} pode usar a função por conta própria.",
				empty: "nenhuma função no ar ainda",
				inputs: "Recebe: {list}",
				optional: "{name} (opcional)",
				byMode: "por ser {mode}",
				byAutomation: "roda pela automação",
				teamOnly: "só a equipe",
				switchLabel: "deixar {name} rodar {artifact}",
				error: "Não deu para mudar quem pode rodar.",
			},
			tools: {
				title: "Sempre sabe",
				lead: "O que faz por conta própria, em toda conversa.",
				count: "{n} ferramenta | {n} ferramentas",
			},
			error: "Não deu para ler o que este agente sabe.",
		},
		tools: {
			title: "Ferramentas",
			count: "({on} de {total})",
			lead: "Só as ferramentas que o modo {mode} enxerga. Desligar uma é o mesmo que ela não existir para este agente.",
			allOn: "Todas ligadas — nada foi restringido ainda.",
			technical: "Detalhes técnicos",
		},
		authority: {
			title: "Autoridade",
			hint: "Um agente tem papel, como uma pessoa. O acesso nunca passa do papel. O que ele vê — tabelas, canais, funis, telas — se define em Configurações → Acessos, para pessoas e agentes igualmente.",
			role: "Roda como {role}",
			changeIn: "Mudar em Configurações → Acessos",
		},
		chat: {
			composePlaceholder: "Escreva uma mensagem…",
			placeholder: "Escreva…  Enter envia, Shift+Enter quebra linha",
			answering: "respondendo…",
			stop: "Parar",
			queue: "Juntar",
			queued: "A seguir: {text}",
			attach: "Anexar arquivo",
			attachHint:
				"Anexar arquivo (imagem, vídeo, áudio, PDF, planilha) — ou só cole (Ctrl+V) e arraste",
			record: "Gravar uma nota de voz",
			stopRecording: "Concluir a gravação",
			cancelRecording: "Descartar a gravação",
			voiceNote: "🎙️ Mensagem de voz",
			removeAudio: "Remover a mensagem de voz",
			noMicrophone: "Não consegui acessar o microfone.",
			removeAttached: "Remover {name}",
			sentImage: "imagem enviada",
			editQuestion: "Corrigir a pergunta",
			cancelEdit: "cancelar",
			resend: "reenviar",
			editAndResend: "editar e reenviar",
			silent: "O agente decidiu não responder desta vez.",
			fromAgent: "Resultado de um agente em segundo plano",
			refused:
				"O provedor do modelo recusou esta mensagem. Ela fica fora do contexto desta conversa — pergunte de outro jeito.",
			longThread:
				"Conversa longa — cada resposta relê tudo. Começar uma nova sai mais barato e mais rápido.",
			toEnd: "↓ ir para o fim",
			showMore: "Mostrar mais",
			showLess: "Mostrar menos",
			copy: "copiar",
			copied: "copiado",
			useAsReply: "usar como resposta",
			regenerate: "regerar",
			deleteMessage: "apagar mensagem",
			confirmDelete: "apagar mesmo?",
			tokens: "· {n} tokens",
			whatIsThis: "O que é isto?",
		},
		suggestions: {
			hello: "Olá 👋",
			toStart: "Para começar",
			orWrite: "Toque numa sugestão ou escreva o que precisar.",
			orWriteAlone: "Escreva uma pergunta, um pedido ou um comando para começar.",
			manage: {
				title: "Perguntas sugeridas",
				lead: "O que o assistente oferece quando é aberto em cada tela. Escritas a partir do seu negócio, e suas para editar.",
				generate: "Escrever a partir do meu negócio",
				generating: "escrevendo…",
				generated: "{n} perguntas escritas.",
				unavailable: "Nenhum modelo respondeu. Nada foi alterado.",
				unreadable: "A resposta não pôde ser lida. Nada foi alterado.",
				failed: "Não deu para escrever: {reason}. Nada foi alterado.",
				anyScreen: "Qualquer tela",
				gone: "Uma tela que não está mais no menu",
				add: "Nova pergunta",
				questionLabel: "A pergunta, com as palavras de quem a faria",
				shortLabel: "Texto curto do botão (opcional)",
				save: "Salvar",
				cancel: "Cancelar",
				remove: "Remover",
				turnOn: "ligar",
				turnOff: "desligar",
				off: "desligada",
				byModel: "escrita pelo modelo",
				empty: "Nada escrito ainda. Peça a partir do seu negócio, ou escreva a primeira.",
				/**
				 * AS DE FÁBRICA, ditas na tela onde elas se mudam (24/09/2026).
				 *
				 * Elas apareciam em toda tela do painel e não existiam aqui: quem
				 * abria esta tela via "Qualquer tela" vazia enquanto o Ben oferecia
				 * cinco linhas lá fora. "Ficar com uma cópia" é o verbo honesto — a
				 * frase de fábrica é código em sete línguas, e editá-la só pode
				 * querer dizer ter a sua.
				 */
				factoryHint:
					"Estas são as de fábrica: o que o Ben oferece em toda tela enquanto este workspace não escreveu nenhuma.",
				adopt: "Ficar com uma cópia minha",
				screenPick: "Tela",
			},
			hintInConversation:
				"Pergunte o que responder. Ele lê a memória do workspace e não fala com o cliente.",
			hintInternal: "Peça ao Ben para operar, criar telas e automatizar seu trabalho.",
			panel: {
				/**
				 * A PRIMEIRA das três, por decisão do dono (24/09): o que um workspace
				 * recém-nascido quer do Ben é o site no ar, não um resumo do dia que
				 * ainda não teve nada dentro.
				 */
				site: "Crie o site do meu negócio",
				/**
				 * UM GESTO NATIVO (desde 05/10 o atendente etiqueta a pessoa da
				 * conversa sem pacote nenhum, e o Ben do painel sempre etiquetou).
				 *
				 * Escrita da boca de QUEM PEDE, como todas as outras: o que se toca
				 * aqui é um pedido ao Ben, não uma frase dele.
				 */
				tagging: "Etiquete as conversas por mim",
				/** Os atalhos do "/" no compositor: a tela com papel `composer.snippet`. */
				snippets: "Crie as respostas rápidas do atendimento",
				screen: "Crie uma tela de orçamentos",
				/**
				 * A pergunta de quem JÁ TEM MOVIMENTO — e a última da oferta, por
				 * isso: numa casa recém-aberta não há registro para estar parado.
				 */
				stalled: "Quais registros estão parados há mais de uma semana?",
				/**
				 * NÃO está na oferta, e é usada como CONTRA-EXEMPLO: ela é a pergunta
				 * certa no painel e a errada com um cliente à espera (`ben-dock.spec`).
				 */
				today: "O que mudou hoje?",
			},
			attendant: {
				discount: "Como você responderia a um pedido de desconto?",
			},
			inConversation: {
				reply: "Sugira uma resposta",
				summarise: "Resuma esta conversa em três linhas",
				known: "O que já sabemos deste contato?",
			},
		},
		missionLive: {
			main: "Ben",
			thinking: "Pensando",
			reading: "Lendo {site}",
			readingPage: "Lendo uma página",
			/** `change_system` numa missão é sempre um PLANO: ninguém ao volante aplica (QA-007). */
			drafting: "Rascunhando a proposta",
			crew: {
				title: "{n} partes da proposta sendo rascunhadas ao mesmo tempo",
				done: "{n} prontas",
				running: "{n} em andamento",
				queued: "{n} na fila",
				chars: "{kb} KB escritos",
				planning: "planejando",
			},
			status: { queued: "na fila", running: "escrevendo", succeeded: "pronto", failed: "falhou" },
		},
		live: {
			acting: "agindo…",
			writing: "escrevendo {tool} — {kb} KB…",
			writingLive: "ao vivo",
			activity: "Atividade",
			reasoningRaw: "raciocínio (dev)",
			tasks: "{n} passos, {running} rodando",
			credits: "{n} cr",
			expand: "abrir",
			collapse: "fechar",
			awaitsHuman: "Esperando a resposta de uma pessoa.",
			running: "em andamento",
			thinking: "pensando…",
			reasoning: "raciocínio",
			closeOutput: "fechar",
			seeOutput: "ver retorno",
		},
		steps: {
			one: "ferramenta",
			many: "ferramentas",
			input: "entrada",
			output: "retorno",
			running: "rodando…",
			fields: "{n} campos",
		},
		approval: {
			approve: "Aprovar",
			done: "Aprovado — os agentes já usam.",
			discarded: "Esta proposta foi descartada.",
			failed: "Não deu para aprovar. Tente de novo ou use Memória → O que a equipe ensinou.",
			replaces: "substitui a linha atual",
			toLibrary: "vai para a Biblioteca",
			forAgent: "só para {agent}",
			title: {
				generic: "Aguardando sua aprovação",
				knowledge: "Conhecimento para os agentes",
			},
		},
		navigate: {
			contact: "contato",
			conversation: "conversa",
			record: "registro",
			screen: "tela",
			back: "voltar",
			open: "abrir",
			toolFile: "arquivo devolvido pela ferramenta",
		},
		sessions: {
			searchFailed: "A busca falhou. Tente novamente.",
			filter: "Filtrar conversas",
			empty: "Nenhuma conversa ainda.",
			noMatch: "Nada com “{term}”.",
			aboutConversation: "Sobre uma conversa",
			conversationBadge: "conversa",
			untitled: "Sem título",
			messages: "{n} mensagens",
			removeLabel: "Apagar {title}",
			thisConversation: "esta conversa",
			confirmRemove: "confirmar",
			remove: "apagar",
			/** O cabeçalho do painel de conversas quando ele ocupa a doca inteira. */
			heading: "Conversas anteriores",
			/** Os grupos por data, no fuso do workspace. */
			groups: {
				today: "Hoje",
				yesterday: "Ontem",
				week: "Últimos 7 dias",
				older: "Mais antigas",
			},
			ago: {
				now: "agora",
				minutes: "{n} min",
				hours: "{n} h",
				days: "{n} d",
			},
		},
		dock: {
			purposeInConversation: "só você vê — nada daqui é enviado",
			confirmRestart: "Recomeçar? Clique de novo",
			noBen:
				"Este workspace não tem um assistente de painel ligado. Ligue um em Agentes para pedir ajuda aqui dentro.",
			purposeInternal: "opere, construa e automatize com o Ben",
			acting: "usando uma ferramenta…",
			typing: "escrevendo…",
			thinking: "pensando…",
			whichAgent: "Com qual agente falar",
			conversations: "Conversas",
			newConversation: "Nova conversa",
			wide: "tela cheia",
			narrow: "voltar para a lateral",
			close: "Fechar",
			/** O botão de histórico com o painel aberto: o clique seguinte devolve o chat. */
			backToChat: "Voltar à conversa",
			hideHistory: "Recolher conversas",
			copilot: "Ben · Copiloto",
			launcher: "Chat com {name}",
			resize: "Arraste para ajustar a largura da conversa",
			tipTitle: "{name} está aqui",
			tipBody:
				"Toque ou clique na orbe destacada na barra superior para abrir o Ben. Clique de novo para fechar o chat. Sua conversa continua aqui.",
			tipShortcut:
				"No teclado: Ctrl + J (Windows/Linux) ou ⌘ + J (Mac) abre ou fecha o Ben quando você não estiver digitando em um campo.",
			tipDone: "Isso! Sempre que precisar, é só clicar.",
			tipDismiss: "Entendi",
		},
		attach: {
			file: "arquivo",
			openFile: "Abrir {name}",
			open: "abrir",
			download: "baixar",
			noPort: "Este link não abre arquivos.",
			downloadFailed: "Não consegui baixar esse arquivo.",
			openFailed: "Não consegui abrir esse arquivo.",
			badType: "{name}: o Ben lê {formats}.",
			tooBig: "{name}: arquivo grande demais (máximo {max} MB).",
			uploadFailed: "{name}: o envio falhou — tente de novo.",
			tooMany: "Até {max} arquivos por mensagem.",
			uploading: "enviando…",
			unreadable: "não consegui ler a imagem",
		},
		rating: {
			up: "a resposta ajudou",
			down: "a resposta não ajudou",
			seeTurn: "ver o turno",
			whatWasWrong: "o que estava errado? (opcional)",
			whatWasWrongLabel: "O que estava errado",
			send: "enviar",
		},
		recovery: {
			checking: "A conexão caiu — conferindo se a resposta chegou…",
			arrived: "A conexão caiu no meio, mas a resposta chegou.",
			lost: "A conexão caiu. Mesmo assim a resposta pode ter sido gerada e guardada — veja o turno antes de mandar de novo.",
			seeTurn: "ver o turno",
		},
		error: {
			expired: "sua sessão expirou — entre de novo",
			couldNotStart: "não foi possível iniciar ({status})",
			somethingWentWrong: "algo deu errado",
			forbidden: "Só um administrador pode fazer isso.",
			unreachable: "Não consegui falar com o servidor.",
			generic: "Não deu para concluir.",
		},
		...agentToolMessages["pt-BR"],
	},
	el: {
		workbench: {
			settingsTab: "Ρυθμίσεις",
			knowledgeTab: "Γνώση",
			chatTab: "Συνομιλία",
			testTab: "Δοκιμή πράκτορα",
			on: "ενεργός",
			off: "ανενεργός",
			turnOn: "Ενεργοποίηση",
			turnOff: "Απενεργοποίηση",
			attendant: {
				title: "Αυτός είναι εκπρόσωπος: μιλάει με τους πελάτες σου.",
				why: "Δεν απαντά εδώ στο πάνελ — αυτός είναι ο κανόνας που εμποδίζει κάποιον απ' έξω να ζητήσει τη διαγραφή ενός πίνακα της βάσης. Για να τον δοκιμάσεις, άνοιξε το chat όπως θα το άνοιγε ένας επισκέπτης.",
				test: "Δοκίμασέ τον ως πελάτης",
				noChat:
					"Δεν έχεις ακόμη ένα chat δημοσιευμένο στον ιστότοπο. Δημιούργησε ένα κανάλι {webchat} στα Κανάλια — δεν χρειάζεται επιβεβαιωμένος αριθμός ούτε εγκεκριμένη εφαρμογή — και δημοσίευσέ το. Παίρνει ένα λεπτό και είναι ο πιο γρήγορος τρόπος να δεις αυτόν τον πράκτορα να δουλεύει.",
				webchat: "webchat",
			},
		},
		resources: {
			heading: "Δυνατότητες",
			files: "Δημιουργία εγγράφου",
			media: "Δημιουργία εικόνας",
			web: "Αναζήτηση στον ιστό",
			filesRequest: "Δημιουργήστε ένα έγγραφο για: ",
			mediaRequest: "Δημιουργήστε μια εικόνα: ",
			webRequest: "Αναζητήστε στον ιστό: ",
		},
		document: {
			saveFailed: "Η αποθήκευση απέτυχε. Το πρόχειρό σας παραμένει εδώ.",
			actions: "Ενέργειες εγγράφου",
			openTab: "Άνοιγμα σε νέα καρτέλα",
			openPdf: "Άνοιγμα PDF",
			tableLimit: "Προεπισκόπηση έως 200 γραμμές και 100 στήλες. Κατεβάστε ολόκληρο το αρχείο.",
			edit: "Επεξεργασία εγγράφου",
			save: "Αποθήκευση νέας έκδοσης",
			cancel: "Ακύρωση",
			compare: "Σύγκριση εκδόσεων",
			conflict:
				"Το αρχείο άλλαξε. Αντιγράψτε τις αλλαγές σας, κλείστε και ανοίξτε την πιο πρόσφατη έκδοση.",

			versions: "Εκδόσεις",
			version: "Έκδοση {version}",
			restore: "Ζήτηση επαναφοράς",
			revise: "Αλλαγή επιλογής",
			reviseRequest: "Στο έγγραφο {name}, αλλάξτε το απόσπασμα:\n“{text}”\nΖητούμενη αλλαγή: ",
			restoreRequest: "Επαναφέρετε την έκδοση {version} του αρχείου {name}.",

			heading: "Έγγραφο",
			close: "Επιστροφή στη συνομιλία",
			preview: "Προεπισκόπηση",
			source: "Πηγαίος κώδικας",
			writing: "Σύνταξη…",
			incomplete: "Ημιτελής προεπισκόπηση",
			loading: "Φόρτωση…",
			failed: "Δεν ήταν δυνατή η προεπισκόπηση. Μπορείτε να κατεβάσετε το αρχείο.",
			open: "Άνοιγμα προεπισκόπησης",
		},
		role: {
			attendant: "εκπρόσωπος",
		},
		sidebar: {
			search: "Αναζήτηση πράκτορα",
			attendants: "Εκπρόσωποι",
			attendantsHint: "Μιλούν με τους πελάτες σου, στα κανάλια.",
			system: "Το σύστημα",
			systemHint: "Ο Ben συνεργάζεται με την ομάδα σας σε όλο το σύστημα.",
			answers: "Απαντά σε: {names}",
			off: "απενεργοποιημένος",
			noMatch: "Κανένας πράκτορας με αυτό το όνομα.",
			newAttendant: "+ Νέος εκπρόσωπος",
		},
		settings: {
			outbound: {
				title: "Μηνύματα προς πελάτες",
				lead: "Ισχύει για ό,τι λέει αυτός ο πράκτορας σε πελάτες — τις απαντήσεις του μέσα στη συνομιλία και όταν {thisAgent}.",
				leadDecides: "αυτός ο πράκτορας αποφασίσει να γράψει σε κάποιον",
				sendLabel: "Στέλνει μόνος του",
				sendHint: "Έτσι λειτουργεί σήμερα. Το μήνυμα φεύγει αμέσως και εμφανίζεται στη συνομιλία.",
				askLabel: "Περιμένει έγκριση",
				askHint:
					"Το μήνυμα μένει στη συνομιλία ως προσχέδιο, με Αποστολή, Επεξεργασία και Απόρριψη. Τίποτα δεν φεύγει μέχρι να πατήσει κάποιος.",
			},
			name: {
				title: "Όνομα",
				lead: "Πώς λέγεται. Είναι το όνομα που εμφανίζεται στον επιλογέα καναλιού και στις συνομιλίες — ο ρόλος του ({role}) δεν αλλάζει με αυτό.",
				label: "Όνομα του πράκτορα",
			},
			tier: {
				title: "Μοντέλο",
				fastHint: "σύντομες απαντήσεις και απλές εργασίες",
				smartHint: "καθημερινή λειτουργία",
				premiumHint: "χτίσιμο δομής, δύσκολες αποφάσεις",
				fast: "Γρήγορο",
				smart: "Ισορροπημένο",
				premium: "Πιο ικανό",
			},
			sex: {
				title: "Φύλο",
				hint: "Επιλέγει τη φωνή με την οποία μιλάει, και το πρόσωπο που του δίνεται.",
				male: "Άνδρας",
				female: "Γυναίκα",
				neutral: "Ουδέτερο",
				none: "Δεν ειπώθηκε",
			},
			copy: {
				nameSuffix: "{name} (αντίγραφο)",
				lead: "Το αντίγραφο γεννιέται {off}: κληρονομεί την περσόνα και τα εργαλεία αυτού του πράκτορα, και τίποτα από αυτό δεν φτάνει σε κανέναν μέχρι να το ενεργοποιήσεις.",
				leadOff: "απενεργοποιημένο",
				namePlaceholder: "Όνομα του αντιγράφου",
				nameLabel: "Όνομα του αντιγράφου",
				codeLabel: "Κωδικός του αντιγράφου",
				action: "Δημιουργία αντιγράφου",
			},
			remove: {
				lead: "Η διαγραφή του {name} δεν σβήνει το ιστορικό: ό,τι έχει απαντήσει μένει στην Κατανάλωση. Αυτό που χάνεται είναι οι ρυθμίσεις — η περσόνα, τα εργαλεία και ο κωδικός {code}, που μπορεί να χρησιμοποιούν αυτοματισμοί και κανάλια.",
				confirm: "Διαγραφή έτσι κι αλλιώς",
				action: "Διαγραφή",
			},
			discard: "Απόρριψη",
		},
		persona: {
			title: "Ποιος είναι",
			filled: "{n} από 5 συμπληρωμένα",
			missingEscalation: "· μένει να πει πότε καλεί ένα άτομο",
			lead: "Αυτό ΠΡΟΣΤΙΘΕΤΑΙ στις οδηγίες του ρόλου — δεν τις αντικαθιστά ποτέ, οπότε τίποτα εδώ δεν απενεργοποιεί τους κανόνες ασφαλείας. Τι είναι η επιχείρηση και τι δεν επιτρέπεται να πει κανείς τα κληρονομεί ήδη από τον Λογαριασμό· μην τα επαναλάβεις εδώ.",
			objective: {
				label: "Για τι υπάρχει",
				hint: "Η εργασία του, σε μία πρόταση. Είναι αυτό που προσπαθεί να πετύχει σε κάθε συνομιλία.",
				placeholder:
					"Να λύνει απορίες όσων έχουν ήδη αγοράσει — παραγγελία, παράδοση και αλλαγή — χωρίς να χρειάζεται να τις προωθεί σε κάποιον.",
			},
			tone: {
				label: "Πώς μιλάει",
				hint: "Ο τρόπος, όχι το περιεχόμενο: φιλικός ή τυπικός, σύντομος ή αναλυτικός.",
				placeholder:
					"Άμεσος και ευγενικός. Σύντομες προτάσεις, χωρίς ορολογία. Απευθύνεται στο άτομο με το μικρό του όνομα και επιβεβαιώνει τι κατάλαβε πριν δράσει.",
			},
			guardrails: {
				label: "Τι δεν κάνει ποτέ",
				hint: "Ισχύει μόνο για ΑΥΤΟΝ τον πράκτορα. Τι δεν επιτρέπεται να πει κανείς στην επιχείρηση βρίσκεται στις Ρυθμίσεις → Χώρος εργασίας → Η επιχείρηση (Σταθεροί κανόνες), και ισχύει ήδη εδώ.",
				placeholder:
					"Ποτέ δεν υπόσχεται ημερομηνία παράδοσης χωρίς να ελέγξει στο σύστημα. Ποτέ δεν προσφέρει έκπτωση. Ποτέ δεν ζητά στοιχεία κάρτας.",
			},
			knowledgeScope: {
				label: "Για τι μιλάει",
				hint: "Το θέμα του. Εκτός αυτού δεν εφευρίσκει τίποτα: το ελέγχει στο σύστημα ή ζητά βοήθεια από την ομάδα.",
				placeholder:
					"Παραγγελίες, προθεσμίες, αλλαγές και επιστροφές. Δεν μιλά για συμβόλαια, για την τιμή νέου προϊόντος, ούτε για νομικά θέματα.",
			},
			escalation: {
				label: "Πότε καλεί ένα άτομο",
				hint: "Το σημάδι για να σταματήσει και να ζητήσει βοήθεια. Χωρίς αυτό, επιμένει μόνος του ακριβώς εκεί όπου η επιμονή είναι το λάθος.",
				placeholder:
					"Όταν κάποιος ζητά ακύρωση, όταν αναφέρει επίσημη καταγγελία, ή όταν η ίδια ερώτηση επανέρχεται για τρίτη φορά.",
			},
			freeText: {
				open: "Άλλες οδηγίες",
				closed: "+ Άλλες οδηγίες",
				lead: "Ελεύθερο κείμενο, που στέλνεται μετά τα πέντε πεδία παραπάνω. Εκεί βρίσκεται ό,τι ήταν ήδη γραμμένο εδώ πριν από αυτά.",
				label: "Ελεύθερο κείμενο περσόνας",
			},
			preview: "Προβολή του κειμένου που λαμβάνει",
		},
		create: {
			titleAttendant: "Νέος εκπρόσωπος",
			titleTeam: "Νέος πράκτορας για την ομάδα",
			leadAttendant:
				"Ένας εκπρόσωπος απαντά στους πελάτες σου στα κανάλια — WhatsApp, ιστότοπος, Instagram.",
			leadTeam:
				"Δουλεύει εδώ μέσα, στο πάνελ, μαζί σου και με την ομάδα — δεν εμφανίζεται σε κανένα κανάλι.",
			nameLabel: "Όνομα",
			namePlaceholder: "Μετά την πώληση",
			codeHint:
				"Σταθερή διεύθυνση: {code} — αυτοματισμοί και κανάλια δείχνουν σε αυτόν και δεν αλλάζει μετά.",
			advanced: "Για προχωρημένους: δημιουργία πράκτορα για την ομάδα, όχι εκπροσώπου",
			hideAdvanced: "Απόκρυψη — θέλω μόνο έναν εκπρόσωπο",
			whoItTalksTo: "Με ποιον μιλάει",
			modeInteraction: "Με τους πελάτες σου",
			modeInteractionHint:
				"Απαντά στα κανάλια. Αυτός είναι που απαντά στο WhatsApp, στον ιστότοπο, στο Instagram.",
			modeOperator: "Με την ομάδα σου",
			modeOperatorHint:
				"Συνεργάζεται με την ομάδα σου: εργάζεται, δημιουργεί οθόνες και αυτοματοποιεί με τα δικαιώματά σου.",
			objectiveLabel: "Για τι υπάρχει",
			objectiveHint:
				"Τον τόνο, τα όρια και το πότε καλεί ένα άτομο τα ρυθμίζεις μετά, στο Ρύθμιση αυτού του πράκτορα.",
		},
		/**
		 * O QUE UM AGENTE SABE, em três títulos — lido de
		 * `/v3/agents/:code/capabilities`, pelas mesmas funções que o turno usa,
		 * para o painel nunca prometer o que o turno não oferece.
		 *
		 * `artifacts` é SABE FAZER: as funções do sistema ao alcance dele, com um
		 * interruptor por agente. `sees` é SABE SOBRE: o que lhe põem na mão antes
		 * de responder. `tools` é SEMPRE SABE: as mãos que vêm com o modo, que
		 * ninguém liga aqui — `ToolMatrix`, logo abaixo, é que é o editor.
		 */
		capabilities: {
			title: "Τι ξέρει: {name}",
			sees: {
				title: "Ξέρει για",
				lead: "Τι του δίνουν στα χέρια πριν απαντήσει.",
				businessProfile: "προφίλ επιχείρησης",
				brandRules: "κανόνες μάρκας",
				persona: "περσόνα",
				memories: "{n} μνήμη | {n} μνήμες",
				documents: "{n} έγγραφο | {n} έγγραφα",
				fixBusiness: "συμπλήρωσέ το στον Λογαριασμό",
				fixBrand: "γράψε τους στον Λογαριασμό",
				fixPersona: "απάντησε παρακάτω",
				fixDocuments: "ανέβασε στη Μνήμη",
			},
			artifacts: {
				title: "Ξέρει να κάνει",
				lead: "Οι συναρτήσεις του συστήματός σου. Αν ανάψεις μία, αυτός ο πράκτορας μπορεί να την τρέξει μόνος του.",
				empty: "καμία συνάρτηση δεν είναι ακόμη στον αέρα",
				inputs: "Παίρνει: {list}",
				optional: "{name} (προαιρετικό)",
				byMode: "επειδή είναι {mode}",
				byAutomation: "τρέχει από αυτοματισμό",
				teamOnly: "μόνο η ομάδα",
				switchLabel: "να τρέχει ο/η {name} το {artifact}",
				error: "Δεν ήταν δυνατή η αλλαγή του ποιος μπορεί να το τρέξει.",
			},
			tools: {
				title: "Πάντα ξέρει",
				lead: "Ό,τι κάνει μόνος του, σε κάθε συνομιλία.",
				count: "{n} εργαλείο | {n} εργαλεία",
			},
			error: "Δεν ήταν δυνατό να διαβαστεί τι ξέρει αυτός ο πράκτορας.",
		},
		tools: {
			title: "Εργαλεία",
			count: "({on} από {total})",
			lead: "Μόνο τα εργαλεία που βλέπει η λειτουργία {mode}. Το να απενεργοποιήσεις ένα είναι σαν να μην υπάρχει για αυτόν τον πράκτορα.",
			allOn: "Όλα ενεργά — τίποτα δεν έχει περιοριστεί ακόμη.",
			technical: "Τεχνικές λεπτομέρειες",
		},
		authority: {
			title: "Εξουσία",
			hint: "Ένας πράκτορας έχει ρόλο, όπως ένα άτομο. Η πρόσβαση δεν ξεπερνά ποτέ τον ρόλο. Τι βλέπει ορίζεται στις Ρυθμίσεις → Πρόσβαση, για άτομα και πράκτορες το ίδιο.",
			role: "Τρέχει ως {role}",
			changeIn: "Αλλαγή στις Ρυθμίσεις → Πρόσβαση",
		},
		chat: {
			composePlaceholder: "Γράψτε ένα μήνυμα…",
			placeholder: "Γράψε…  Enter στέλνει, Shift+Enter αλλάζει γραμμή",
			answering: "απαντά…",
			stop: "Διακοπή",
			queue: "Στη σειρά",
			queued: "Μετά: {text}",
			attach: "Επισύναψη αρχείου",
			attachHint:
				"Επισύναψη αρχείου (εικόνα, βίντεο, ήχος, PDF, υπολογιστικό φύλλο) — ή απλώς επικόλλησε (Ctrl+V) και σύρε",
			record: "Ηχογράφησε ένα φωνητικό",
			stopRecording: "Ολοκλήρωση ηχογράφησης",
			cancelRecording: "Απόρριψη ηχογράφησης",
			voiceNote: "🎙️ Φωνητικό μήνυμα",
			removeAudio: "Αφαίρεση του φωνητικού",
			noMicrophone: "Δεν ήταν δυνατή η πρόσβαση στο μικρόφωνο.",
			removeAttached: "Αφαίρεση {name}",
			sentImage: "συνημμένη εικόνα",
			editQuestion: "Διόρθωση της ερώτησης",
			cancelEdit: "ακύρωση",
			resend: "επαναποστολή",
			editAndResend: "επεξεργασία και επαναποστολή",
			silent: "Ο πράκτορας επέλεξε να μην απαντήσει αυτή τη φορά.",
			fromAgent: "Αποτέλεσμα από πράκτορα στο παρασκήνιο",
			refused:
				"Ο πάροχος του μοντέλου αρνήθηκε αυτό το μήνυμα. Μένει εκτός του πλαισίου της συνομιλίας — ρώτησε με άλλα λόγια.",
			longThread:
				"Μεγάλη συνομιλία — κάθε απάντηση την ξαναδιαβάζει ολόκληρη. Το να ξεκινήσεις μια νέα είναι φθηνότερο και γρηγορότερο.",
			toEnd: "↓ στο τέλος",
			showMore: "Περισσότερα",
			showLess: "Λιγότερα",
			copy: "αντιγραφή",
			copied: "αντιγράφηκε",
			useAsReply: "χρήση ως απάντηση",
			regenerate: "αναδημιουργία",
			deleteMessage: "διαγραφή μηνύματος",
			confirmDelete: "σίγουρα διαγραφή;",
			tokens: "· {n} tokens",
			whatIsThis: "Τι είναι αυτό;",
		},
		suggestions: {
			hello: "Γεια 👋",
			toStart: "Για αρχή",
			orWrite: "Πατήστε μια πρόταση ή γράψτε αυτό που χρειάζεστε.",
			orWriteAlone: "Γράψτε μια ερώτηση, ένα αίτημα ή μια εντολή για να ξεκινήσετε.",
			manage: {
				title: "Προτεινόμενες ερωτήσεις",
				lead: "Τι προσφέρει ο βοηθός όταν ανοίγει σε κάθε οθόνη. Γραμμένες από την επιχείρησή σας, δικές σας να τις αλλάξετε.",
				generate: "Γράψ' τες από την επιχείρησή μου",
				generating: "γράφει…",
				generated: "Γράφτηκαν {n} ερωτήσεις.",
				unavailable: "Κανένα μοντέλο δεν απάντησε. Δεν άλλαξε τίποτα.",
				unreadable: "Η απάντηση δεν διαβάστηκε. Δεν άλλαξε τίποτα.",
				failed: "Δεν ήταν δυνατή η συγγραφή: {reason}. Δεν άλλαξε τίποτα.",
				anyScreen: "Οποιαδήποτε οθόνη",
				gone: "Οθόνη που δεν υπάρχει πια στο μενού",
				add: "Νέα ερώτηση",
				questionLabel: "Η ερώτηση, με τα λόγια αυτού που θα τη ρωτούσε",
				shortLabel: "Σύντομο κείμενο για το κουμπί (προαιρετικό)",
				save: "Αποθήκευση",
				cancel: "Άκυρο",
				remove: "Αφαίρεση",
				turnOn: "ενεργοποίηση",
				turnOff: "απενεργοποίηση",
				off: "ανενεργή",
				byModel: "γραμμένη από το μοντέλο",
				empty: "Δεν έχει γραφτεί τίποτα. Ζητήστε τες από την επιχείρησή σας ή γράψτε την πρώτη.",
				/** As de fábrica — ver o bloco pt-BR para o porquê do verbo. */
				factoryHint:
					"Αυτές είναι οι εργοστασιακές: ό,τι προσφέρει ο Ben σε κάθε οθόνη όσο αυτός ο χώρος δεν έχει γράψει καμία.",
				adopt: "Κράτα ένα αντίγραφο δικό μου",
				screenPick: "Οθόνη",
			},
			hintInConversation:
				"Ρώτησέ τον τι να απαντήσεις. Διαβάζει τη μνήμη του χώρου εργασίας και δεν μιλά στον πελάτη.",
			hintInternal:
				"Ζήτησε από τον Ben να εργαστεί, να δημιουργήσει οθόνες και να αυτοματοποιήσει εργασίες.",
			panel: {
				/**
				 * A PRIMEIRA das três, por decisão do dono (24/09): o que um workspace
				 * recém-nascido quer do Ben é o site no ar, não um resumo do dia que
				 * ainda não teve nada dentro.
				 */
				site: "Φτιάξε τον ιστότοπο της επιχείρησής μου",
				/**
				 * UM GESTO NATIVO (desde 05/10 o atendente etiqueta a pessoa da
				 * conversa sem pacote nenhum, e o Ben do painel sempre etiquetou).
				 *
				 * Escrita da boca de QUEM PEDE, como todas as outras: o que se toca
				 * aqui é um pedido ao Ben, não uma frase dele.
				 */
				tagging: "Βάλε ετικέτες στις συνομιλίες για μένα",
				/** Os atalhos do "/" no compositor: a tela com papel `composer.snippet`. */
				snippets: "Φτιάξε τις γρήγορες απαντήσεις της εξυπηρέτησης",
				screen: "Φτιάξε μια οθόνη προσφορών",
				/**
				 * A pergunta de quem JÁ TEM MOVIMENTO — e a última da oferta, por
				 * isso: numa casa recém-aberta não há registro para estar parado.
				 */
				stalled: "Ποιες εγγραφές είναι στάσιμες πάνω από μία εβδομάδα;",
				/**
				 * NÃO está na oferta, e é usada como CONTRA-EXEMPLO: ela é a pergunta
				 * certa no painel e a errada com um cliente à espera (`ben-dock.spec`).
				 */
				today: "Τι άλλαξε σήμερα;",
			},
			attendant: {
				discount: "Πώς θα απαντούσες σε ένα αίτημα για έκπτωση;",
			},
			inConversation: {
				reply: "Πρότεινε μια απάντηση",
				summarise: "Σύνοψε αυτή τη συνομιλία σε τρεις γραμμές",
				known: "Τι ξέρουμε ήδη για αυτόν τον πελάτη;",
			},
		},
		missionLive: {
			main: "Ben",
			thinking: "Σκέψη",
			reading: "Διαβάζει {site}",
			readingPage: "Διαβάζει μια σελίδα",
			/** `change_system` numa missão é sempre um PLANO: ninguém ao volante aplica (QA-007). */
			drafting: "Σύνταξη της πρότασης",
			crew: {
				title: "{n} μέρη της πρότασης συντάσσονται ταυτόχρονα",
				done: "{n} έτοιμα",
				running: "{n} σε εξέλιξη",
				queued: "{n} σε αναμονή",
				chars: "{kb} KB γραμμένα",
				planning: "σχεδιάζει",
			},
			status: { queued: "σε αναμονή", running: "γράφει", succeeded: "έτοιμο", failed: "απέτυχε" },
		},
		live: {
			acting: "ενεργεί…",
			writing: "γράφει {tool} — {kb} KB…",
			writingLive: "ζωντανά",
			activity: "Δραστηριότητα",
			reasoningRaw: "συλλογισμός (dev)",
			tasks: "{n} βήματα, {running} σε εξέλιξη",
			credits: "{n} cr",
			expand: "άνοιγμα",
			collapse: "κλείσιμο",
			awaitsHuman: "Αναμονή απάντησης από άνθρωπο.",
			running: "σε εξέλιξη",
			thinking: "σκέφτεται…",
			reasoning: "συλλογισμός",
			closeOutput: "κλείσιμο",
			seeOutput: "προβολή αποτελέσματος",
		},
		steps: {
			one: "εργαλείο",
			many: "εργαλεία",
			input: "είσοδος",
			output: "αποτέλεσμα",
			running: "εκτελείται…",
			fields: "{n} πεδία",
		},
		approval: {
			approve: "Έγκριση",
			done: "Εγκρίθηκε — οι πράκτορες το χρησιμοποιούν ήδη.",
			discarded: "Αυτή η πρόταση απορρίφθηκε.",
			failed: "Δεν ήταν δυνατή η έγκριση. Δοκίμασε ξανά ή άνοιξε Μνήμη → Τι δίδαξε η ομάδα.",
			replaces: "αντικαθιστά την τρέχουσα γραμμή",
			toLibrary: "πηγαίνει στη Βιβλιοθήκη",
			forAgent: "μόνο για {agent}",
			title: {
				generic: "Περιμένει την έγκρισή σου",
				knowledge: "Γνώση για τους πράκτορες",
			},
		},
		navigate: {
			contact: "επαφή",
			conversation: "συνομιλία",
			record: "εγγραφή",
			screen: "οθόνη",
			back: "πίσω",
			open: "άνοιγμα",
			toolFile: "αρχείο που επέστρεψε το εργαλείο",
		},
		sessions: {
			searchFailed: "Η αναζήτηση απέτυχε. Δοκιμάστε ξανά.",
			filter: "Φιλτράρισμα συνομιλιών",
			empty: "Καμία συνομιλία ακόμη.",
			noMatch: "Τίποτα με «{term}».",
			aboutConversation: "Σχετικά με μια συνομιλία",
			conversationBadge: "συνομιλία",
			untitled: "Χωρίς τίτλο",
			messages: "{n} μηνύματα",
			removeLabel: "Διαγραφή {title}",
			thisConversation: "αυτής της συνομιλίας",
			confirmRemove: "επιβεβαίωση",
			remove: "διαγραφή",
			/** O cabeçalho do painel de conversas quando ele ocupa a doca inteira. */
			heading: "Προηγούμενες συνομιλίες",
			/** Os grupos por data, no fuso do workspace. */
			groups: {
				today: "Σήμερα",
				yesterday: "Χθες",
				week: "Τελευταίες 7 ημέρες",
				older: "Παλαιότερες",
			},
			ago: {
				now: "τώρα",
				minutes: "{n} λ.",
				hours: "{n} ώ.",
				days: "{n} ημ.",
			},
		},
		dock: {
			purposeInConversation: "μόνο εσύ το βλέπεις — τίποτα από εδώ δεν στέλνεται",
			confirmRestart: "Νέα αρχή; Κάνε ξανά κλικ",
			noBen:
				"Αυτός ο χώρος εργασίας δεν έχει ενεργό πράκτορα για το πάνελ. Ενεργοποίησε έναν στους Πράκτορες για να ζητάς βοήθεια εδώ μέσα.",
			purposeInternal: "εργασία, κατασκευή και αυτοματοποίηση",
			acting: "χρησιμοποιεί ένα εργαλείο…",
			typing: "γράφει…",
			thinking: "σκέφτεται…",
			whichAgent: "Με ποιον πράκτορα να μιλήσεις",
			conversations: "Συνομιλίες",
			newConversation: "Νέα συνομιλία",
			wide: "πλήρης οθόνη",
			narrow: "επιστροφή στο πλάι",
			close: "Κλείσιμο",
			/** O botão de histórico com o painel aberto: o clique seguinte devolve o chat. */
			backToChat: "Πίσω στη συνομιλία",
			hideHistory: "Απόκρυψη συνομιλιών",
			copilot: "Ben · Βοηθός συνομιλίας",
			launcher: "Συνομιλία με {name}",
			resize: "Σύρε για να αλλάξεις το πλάτος της συζήτησης",
			tipTitle: "Ο/Η {name} είναι εδώ",
			tipBody:
				"Πατήστε τη φωτισμένη σφαίρα στην επάνω γραμμή για να ανοίξετε τον Ben. Πατήστε ξανά για κλείσιμο. Η συνομιλία διατηρείται.",
			tipShortcut:
				"Με πληκτρολόγιο: Ctrl + J (Windows/Linux) ή ⌘ + J (Mac) ανοίγει ή κλείνει τον Ben όταν δεν πληκτρολογείτε σε πεδίο.",
			tipDone: "Αυτό είναι! Όποτε χρειάζεσαι βοήθεια, απλώς κάνε κλικ.",
			tipDismiss: "Κατάλαβα",
		},
		attach: {
			file: "αρχείο",
			openFile: "Άνοιγμα {name}",
			open: "άνοιγμα",
			download: "λήψη",
			noPort: "Αυτός ο σύνδεσμος δεν ανοίγει αρχεία.",
			downloadFailed: "Δεν ήταν δυνατή η λήψη αυτού του αρχείου.",
			openFailed: "Δεν ήταν δυνατό το άνοιγμα αυτού του αρχείου.",
			badType: "{name}: ο Ben διαβάζει {formats}.",
			tooBig: "{name}: πολύ μεγάλο αρχείο ({max} MB το πολύ).",
			uploadFailed: "{name}: η αποστολή απέτυχε — δοκίμασε ξανά.",
			tooMany: "Έως {max} αρχεία ανά μήνυμα.",
			uploading: "αποστολή…",
			unreadable: "δεν ήταν δυνατή η ανάγνωση της εικόνας",
		},
		rating: {
			up: "η απάντηση βοήθησε",
			down: "η απάντηση δεν βοήθησε",
			seeTurn: "προβολή του γύρου",
			whatWasWrong: "τι ήταν λάθος; (προαιρετικό)",
			whatWasWrongLabel: "Τι ήταν λάθος",
			send: "αποστολή",
		},
		recovery: {
			checking: "Η σύνδεση έπεσε — γίνεται έλεγχος αν έφτασε η απάντηση…",
			arrived: "Η σύνδεση έπεσε στη μέση, αλλά η απάντηση έφτασε.",
			lost: "Η σύνδεση έπεσε. Η απάντηση μπορεί παρ’ όλα αυτά να δημιουργήθηκε και να αποθηκεύτηκε — δες τον γύρο πριν στείλεις ξανά.",
			seeTurn: "προβολή του γύρου",
		},
		error: {
			expired: "η συνεδρία σου έληξε — συνδέσου ξανά",
			couldNotStart: "δεν ήταν δυνατή η εκκίνηση ({status})",
			somethingWentWrong: "κάτι πήγε στραβά",
			forbidden: "Μόνο ένας διαχειριστής μπορεί να το κάνει αυτό.",
			unreachable: "Δεν ήταν δυνατή η επικοινωνία με τον διακομιστή.",
			generic: "Δεν ήταν δυνατή η ολοκλήρωση.",
		},
		...agentToolMessages.el,
	},
	es: {
		workbench: {
			settingsTab: "Ajustes",
			knowledgeTab: "Conocimiento",
			chatTab: "Conversar",
			testTab: "Probar agente",
			on: "activo",
			off: "inactivo",
			turnOn: "Activar",
			turnOff: "Desactivar",
			attendant: {
				title: "Este es un agente de atención: habla con tus clientes.",
				why: "No responde aquí en el panel — esa es la regla que impide que alguien de fuera le pida borrar una tabla. Para probarlo, abre el chat como lo abriría un visitante.",
				test: "Probar como cliente",
				noChat:
					"Todavía no tienes un chat publicado en la web. Crea un canal {webchat} en Canales — no hace falta número verificado ni app aprobada — y publícalo. Lleva un minuto y es la forma más rápida de ver a este agente trabajando.",
				webchat: "chat web",
			},
		},
		resources: {
			heading: "Recursos",
			files: "Crear documento",
			media: "Crear imagen",
			web: "Buscar en la web",
			filesRequest: "Crea un documento sobre: ",
			mediaRequest: "Crea una imagen de: ",
			webRequest: "Busca en la web: ",
		},
		document: {
			saveFailed: "No se pudo guardar. Tu borrador sigue aquí.",
			actions: "Acciones del documento",
			openTab: "Abrir en otra pestaña",
			openPdf: "Abrir PDF",
			tableLimit: "Vista limitada a 200 filas y 100 columnas. Descarga el archivo completo.",
			edit: "Editar documento",
			save: "Guardar nueva versión",
			cancel: "Cancelar",
			compare: "Comparar versiones",
			conflict:
				"El archivo cambió mientras editabas. Copia tus cambios, cierra y abre la versión más reciente.",

			versions: "Versiones",
			version: "Versión {version}",
			restore: "Pedir restauración",
			revise: "Ajustar selección",
			reviseRequest: "En el documento {name}, ajusta este fragmento:\n“{text}”\nCambio deseado: ",
			restoreRequest: "Restaura la versión {version} del archivo {name}.",

			heading: "Documento",
			close: "Volver al chat",
			preview: "Vista previa",
			source: "Código / fuente",
			writing: "Escribiendo…",
			incomplete: "Vista previa incompleta",
			loading: "Cargando vista previa…",
			failed: "No se pudo mostrar el archivo. Puedes descargarlo.",
			open: "Abrir vista previa",
		},
		role: {
			attendant: "agente de atención",
		},
		sidebar: {
			search: "Buscar agente",
			attendants: "Agentes de atención",
			attendantsHint: "Hablan con tus clientes, en los canales.",
			system: "El sistema",
			systemHint: "Ben trabaja con tu equipo en todo el sistema.",
			answers: "Responde en: {names}",
			off: "desactivado",
			noMatch: "Ningún agente con ese nombre.",
			newAttendant: "+ Nuevo agente de atención",
		},
		settings: {
			outbound: {
				title: "Mensajes a los clientes",
				lead: "Vale para todo lo que este agente dice a los clientes — sus respuestas dentro de la conversación y cuando {thisAgent}.",
				leadDecides: "este agente decide escribir a alguien",
				sendLabel: "Envía por su cuenta",
				sendHint: "Cómo funciona hoy. El mensaje sale al momento y aparece en la conversación.",
				askLabel: "Espera aprobación",
				askHint:
					"El mensaje se queda en la conversación como borrador, con Enviar, Editar y Rechazar. No sale nada hasta que alguien hace clic.",
			},
			name: {
				title: "Nombre",
				lead: "Cómo se llama. Es el nombre que aparece en el selector de canal y en las conversaciones — su papel ({role}) no cambia con eso.",
				label: "Nombre del agente",
			},
			tier: {
				title: "Modelo",
				fastHint: "respuestas cortas y tareas sencillas",
				smartHint: "operación del día a día",
				premiumHint: "montar estructura, decisiones difíciles",
				fast: "Rápido",
				smart: "Equilibrado",
				premium: "Más capaz",
			},
			sex: {
				title: "Sexo",
				hint: "Elige la voz con la que habla, y el rostro que recibe.",
				male: "Masculino",
				female: "Femenino",
				neutral: "Neutro",
				none: "No dicho",
			},
			copy: {
				nameSuffix: "{name} (copia)",
				lead: "La copia nace {off}: hereda la personalidad y las herramientas de este agente, y nada suyo llega a nadie hasta que la actives.",
				leadOff: "desactivada",
				namePlaceholder: "Nombre de la copia",
				nameLabel: "Nombre de la copia",
				codeLabel: "Código de la copia",
				action: "Duplicar",
			},
			remove: {
				lead: "Eliminar {name} no borra el historial: todo lo que ha respondido sigue en Consumo. Lo que se va es la configuración — personalidad, herramientas y el código {code}, que puede que automatizaciones y canales estén usando.",
				confirm: "Eliminar de todos modos",
				action: "Eliminar",
			},
			discard: "Descartar",
		},
		persona: {
			title: "Quién es",
			filled: "{n} de 5 contestadas",
			missingEscalation: "· todavía falta decir cuándo llamar a una persona",
			lead: "Esto se SUMA a las instrucciones del papel — nunca las sustituye, así que nada de aquí desactiva las reglas de seguridad. Lo que es la empresa y lo que nadie puede decir ya lo hereda de la Cuenta; no lo repitas aquí.",
			objective: {
				label: "Para qué existe",
				hint: "Su tarea, en una frase. Es lo que intenta cumplir en cada conversación.",
				placeholder:
					"Resolver dudas de quien ya ha comprado — pedido, entrega y cambio — sin tener que pasarlo a nadie.",
			},
			tone: {
				label: "Cómo habla",
				hint: "La manera, no el contenido: cercano o formal, corto o detallado.",
				placeholder:
					"Directo y cordial. Frases cortas, sin jerga. Llama a la persona por su nombre y confirma lo que ha entendido antes de actuar.",
			},
			guardrails: {
				label: "Lo que nunca hace",
				hint: "Vale solo para ESTE agente. Lo que nadie de la empresa puede decir está en Ajustes → Espacio de trabajo → El negocio (Reglas fijas), y ya se aplica aquí.",
				placeholder:
					"Nunca promete una fecha de entrega sin comprobarla en el sistema. Nunca ofrece descuento. Nunca pide datos de la tarjeta.",
			},
			knowledgeScope: {
				label: "De qué habla",
				hint: "Su tema. Fuera de eso no se inventa nada: lo consulta en el sistema o pide ayuda al equipo.",
				placeholder:
					"Pedidos, plazos, cambios y devoluciones. No habla de contratos, del precio de un producto nuevo ni de asuntos legales.",
			},
			escalation: {
				label: "Cuándo llama a una persona",
				hint: "La señal para parar y pedir ayuda. Sin eso, insiste por su cuenta justo en el caso en que insistir es el error.",
				placeholder:
					"Cuando alguien pide cancelar, cuando menciona una reclamación formal, o cuando la misma pregunta vuelve por tercera vez.",
			},
			freeText: {
				open: "Otras instrucciones",
				closed: "+ Otras instrucciones",
				lead: "Texto libre, enviado después de los cinco campos de arriba. Es donde queda lo que ya estaba escrito aquí antes que ellos.",
				label: "Texto libre de la personalidad",
			},
			preview: "Ver el texto que recibe",
		},
		create: {
			titleAttendant: "Nuevo agente de atención",
			titleTeam: "Nuevo agente para el equipo",
			leadAttendant:
				"Un agente de atención responde a tus clientes en los canales — WhatsApp, web, Instagram.",
			leadTeam:
				"Trabaja aquí dentro, en el panel, contigo y con el equipo — no aparece en ningún canal.",
			nameLabel: "Nombre",
			namePlaceholder: "Posventa",
			codeHint:
				"Dirección fija: {code} — automatizaciones y canales apuntan a ella y no cambia después.",
			advanced: "Avanzado: crear un agente para el equipo, no de atención",
			hideAdvanced: "Ocultar — solo quiero un agente de atención",
			whoItTalksTo: "Con quién habla",
			modeInteraction: "Con tus clientes",
			modeInteractionHint:
				"Atiende en los canales. Es el que responde en WhatsApp, en la web, en Instagram.",
			modeOperator: "Con tu equipo",
			modeOperatorHint:
				"Trabaja con tu equipo: opera, crea pantallas y automatiza con tus permisos.",
			objectiveLabel: "Para qué existe",
			objectiveHint:
				"El tono, los límites y cuándo llamar a una persona los ajustas después, en Ajustar este agente.",
		},
		/**
		 * O QUE UM AGENTE SABE, em três títulos — lido de
		 * `/v3/agents/:code/capabilities`, pelas mesmas funções que o turno usa,
		 * para o painel nunca prometer o que o turno não oferece.
		 *
		 * `artifacts` é SABE FAZER: as funções do sistema ao alcance dele, com um
		 * interruptor por agente. `sees` é SABE SOBRE: o que lhe põem na mão antes
		 * de responder. `tools` é SEMPRE SABE: as mãos que vêm com o modo, que
		 * ninguém liga aqui — `ToolMatrix`, logo abaixo, é que é o editor.
		 */
		capabilities: {
			title: "Lo que {name} sabe",
			sees: {
				title: "Sabe sobre",
				lead: "Lo que le ponen en la mano antes de responder.",
				businessProfile: "ficha del negocio",
				brandRules: "reglas de marca",
				persona: "persona",
				memories: "{n} memoria | {n} memorias",
				documents: "{n} documento | {n} documentos",
				fixBusiness: "rellenarla en Cuenta",
				fixBrand: "escribirlas en Cuenta",
				fixPersona: "responder abajo",
				fixDocuments: "subir en Memoria",
			},
			artifacts: {
				title: "Sabe hacer",
				lead: "Las funciones de tu sistema. Encender una deja que este agente la ejecute solo.",
				empty: "todavía no hay ninguna función en el aire",
				inputs: "Recibe: {list}",
				optional: "{name} (opcional)",
				byMode: "por ser {mode}",
				byAutomation: "roda pela automação",
				teamOnly: "solo el equipo",
				switchLabel: "dejar que {name} ejecute {artifact}",
				error: "No se pudo cambiar quién puede ejecutarla.",
			},
			tools: {
				title: "Siempre sabe",
				lead: "Lo que hace por su cuenta, en cada conversación.",
				count: "{n} herramienta | {n} herramientas",
			},
			error: "No se pudo leer lo que sabe este agente.",
		},
		tools: {
			title: "Herramientas",
			count: "({on} de {total})",
			lead: "Solo las herramientas que ve el modo {mode}. Desactivar una es lo mismo que si no existiera para este agente.",
			allOn: "Todas activadas — todavía no se ha restringido nada.",
			technical: "Detalles técnicos",
		},
		authority: {
			title: "Autoridad",
			hint: "Un agente tiene rol, como una persona. El acceso nunca supera el rol. Lo que ve se define en Ajustes → Accesos, para personas y agentes por igual.",
			role: "Se ejecuta como {role}",
			changeIn: "Cambiar en Ajustes → Accesos",
		},
		chat: {
			composePlaceholder: "Escribe un mensaje…",
			placeholder: "Escribe…  Enter envía, Shift+Enter salta de línea",
			answering: "respondiendo…",
			stop: "Parar",
			queue: "Encolar",
			queued: "A continuación: {text}",
			attach: "Adjuntar un archivo",
			attachHint:
				"Adjuntar un archivo (imagen, video, audio, PDF, hoja de cálculo) — o solo pega (Ctrl+V) y arrastra",
			record: "Grabar una nota de voz",
			stopRecording: "Terminar la grabación",
			cancelRecording: "Descartar la grabación",
			voiceNote: "🎙️ Nota de voz",
			removeAudio: "Quitar la nota de voz",
			noMicrophone: "No se ha podido acceder al micrófono.",
			removeAttached: "Quitar {name}",
			sentImage: "imagen adjunta",
			editQuestion: "Corregir la pregunta",
			cancelEdit: "cancelar",
			resend: "reenviar",
			editAndResend: "editar y reenviar",
			silent: "El agente eligió no responder esta vez.",
			fromAgent: "Resultado de un agente en segundo plano",
			refused:
				"El proveedor del modelo rechazó este mensaje. Queda fuera del contexto de esta conversación — pregunta de otra forma.",
			longThread:
				"Conversación larga — cada respuesta la relee entera. Empezar una nueva sale más barato y más rápido.",
			toEnd: "↓ ir al final",
			showMore: "Mostrar más",
			showLess: "Mostrar menos",
			copy: "copiar",
			copied: "copiado",
			useAsReply: "usar como respuesta",
			regenerate: "regenerar",
			deleteMessage: "borrar mensaje",
			confirmDelete: "¿borrar de verdad?",
			tokens: "· {n} tokens",
			whatIsThis: "¿Qué es esto?",
		},
		suggestions: {
			hello: "Hola 👋",
			toStart: "Para empezar",
			orWrite: "Toca una sugerencia o escribe lo que necesites.",
			orWriteAlone: "Escribe una pregunta, una petición o una orden para empezar.",
			manage: {
				title: "Preguntas sugeridas",
				lead: "Lo que el asistente ofrece cuando se abre en cada pantalla. Escritas a partir de tu negocio, y tuyas para editar.",
				generate: "Escribirlas desde mi negocio",
				generating: "escribiendo…",
				generated: "{n} preguntas escritas.",
				unavailable: "Ningún modelo respondió. No se cambió nada.",
				unreadable: "No se pudo leer la respuesta. No se cambió nada.",
				failed: "No se pudieron escribir: {reason}. No se cambió nada.",
				anyScreen: "Cualquier pantalla",
				gone: "Una pantalla que ya no está en el menú",
				add: "Nueva pregunta",
				questionLabel: "La pregunta, con las palabras de quien la haría",
				shortLabel: "Texto corto del botón (opcional)",
				save: "Guardar",
				cancel: "Cancelar",
				remove: "Quitar",
				turnOn: "activar",
				turnOff: "desactivar",
				off: "desactivada",
				byModel: "escrita por el modelo",
				empty: "Nada escrito todavía. Pídelas desde tu negocio o escribe la primera.",
				/** As de fábrica — ver o bloco pt-BR para o porquê do verbo. */
				factoryHint:
					"Estas son las de fábrica: lo que Ben ofrece en cada pantalla mientras este espacio no ha escrito ninguna.",
				adopt: "Quedarme con una copia mía",
				screenPick: "Pantalla",
			},
			hintInConversation:
				"Pregúntale qué responder. Lee la memoria del espacio de trabajo y no habla con el cliente.",
			hintInternal: "Pide a Ben operar, crear pantallas y automatizar tu trabajo.",
			panel: {
				/**
				 * A PRIMEIRA das três, por decisão do dono (24/09): o que um workspace
				 * recém-nascido quer do Ben é o site no ar, não um resumo do dia que
				 * ainda não teve nada dentro.
				 */
				site: "Crea el sitio web de mi negocio",
				/**
				 * UM GESTO NATIVO (desde 05/10 o atendente etiqueta a pessoa da
				 * conversa sem pacote nenhum, e o Ben do painel sempre etiquetou).
				 *
				 * Escrita da boca de QUEM PEDE, como todas as outras: o que se toca
				 * aqui é um pedido ao Ben, não uma frase dele.
				 */
				tagging: "Etiqueta las conversaciones por mí",
				/** Os atalhos do "/" no compositor: a tela com papel `composer.snippet`. */
				snippets: "Crea las respuestas rápidas de atención",
				screen: "Crea una pantalla de presupuestos",
				/**
				 * A pergunta de quem JÁ TEM MOVIMENTO — e a última da oferta, por
				 * isso: numa casa recém-aberta não há registro para estar parado.
				 */
				stalled: "¿Qué registros llevan parados más de una semana?",
				/**
				 * NÃO está na oferta, e é usada como CONTRA-EXEMPLO: ela é a pergunta
				 * certa no painel e a errada com um cliente à espera (`ben-dock.spec`).
				 */
				today: "¿Qué ha cambiado hoy?",
			},
			attendant: {
				discount: "¿Cómo responderías a una petición de descuento?",
			},
			inConversation: {
				reply: "Sugiere una respuesta",
				summarise: "Resume esta conversación en tres líneas",
				known: "¿Qué sabemos ya de este cliente?",
			},
		},
		missionLive: {
			main: "Ben",
			thinking: "Pensando",
			reading: "Leyendo {site}",
			readingPage: "Leyendo una página",
			/** `change_system` numa missão é sempre um PLANO: ninguém ao volante aplica (QA-007). */
			drafting: "Redactando la propuesta",
			crew: {
				title: "{n} partes de la propuesta redactándose al mismo tiempo",
				done: "{n} listas",
				running: "{n} en curso",
				queued: "{n} en cola",
				chars: "{kb} KB escritos",
				planning: "planificando",
			},
			status: { queued: "en cola", running: "escribiendo", succeeded: "listo", failed: "falló" },
		},
		live: {
			acting: "actuando…",
			writing: "escribiendo {tool} — {kb} KB…",
			writingLive: "en vivo",
			activity: "Actividad",
			reasoningRaw: "razonamiento (dev)",
			tasks: "{n} pasos, {running} en curso",
			credits: "{n} cr",
			expand: "abrir",
			collapse: "cerrar",
			awaitsHuman: "Esperando la respuesta de una persona.",
			running: "en curso",
			thinking: "pensando…",
			reasoning: "razonamiento",
			closeOutput: "cerrar",
			seeOutput: "ver la salida",
		},
		steps: {
			one: "herramienta",
			many: "herramientas",
			input: "entrada",
			output: "salida",
			running: "ejecutando…",
			fields: "{n} campos",
		},
		approval: {
			approve: "Aprobar",
			done: "Aprobado — los agentes ya lo usan.",
			discarded: "Esta propuesta fue descartada.",
			failed: "No se pudo aprobar. Inténtalo de nuevo o usa Memoria → Lo que enseñó el equipo.",
			replaces: "reemplaza la línea actual",
			toLibrary: "va a la Biblioteca",
			forAgent: "solo para {agent}",
			title: {
				generic: "Esperando tu aprobación",
				knowledge: "Conocimiento para los agentes",
			},
		},
		navigate: {
			contact: "contacto",
			conversation: "conversación",
			record: "registro",
			screen: "pantalla",
			back: "volver",
			open: "abrir",
			toolFile: "archivo devuelto por la herramienta",
		},
		sessions: {
			searchFailed: "La búsqueda falló. Inténtalo de nuevo.",
			filter: "Filtrar conversaciones",
			empty: "Ninguna conversación todavía.",
			noMatch: "Nada con “{term}”.",
			aboutConversation: "Sobre una conversación",
			conversationBadge: "conversación",
			untitled: "Sin título",
			messages: "{n} mensajes",
			removeLabel: "Eliminar {title}",
			thisConversation: "esta conversación",
			confirmRemove: "confirmar",
			remove: "eliminar",
			/** O cabeçalho do painel de conversas quando ele ocupa a doca inteira. */
			heading: "Conversaciones anteriores",
			/** Os grupos por data, no fuso do workspace. */
			groups: {
				today: "Hoy",
				yesterday: "Ayer",
				week: "Últimos 7 días",
				older: "Más antiguas",
			},
			ago: {
				now: "ahora",
				minutes: "{n} min",
				hours: "{n} h",
				days: "{n} d",
			},
		},
		dock: {
			purposeInConversation: "solo lo ves tú — nada de aquí se envía",
			confirmRestart: "¿Empezar de nuevo? Pulsa otra vez",
			noBen:
				"Este espacio de trabajo no tiene ningún asistente de panel activo. Activa uno en Agentes para pedir ayuda aquí dentro.",
			purposeInternal: "opera, construye y automatiza",
			acting: "usando una herramienta…",
			typing: "escribiendo…",
			thinking: "pensando…",
			whichAgent: "Con qué agente hablar",
			conversations: "Conversaciones",
			newConversation: "Nueva conversación",
			wide: "pantalla completa",
			narrow: "volver al lateral",
			close: "Cerrar",
			/** O botão de histórico com o painel aberto: o clique seguinte devolve o chat. */
			backToChat: "Volver a la conversación",
			hideHistory: "Ocultar conversaciones",
			copilot: "Ben · Copiloto",
			launcher: "Chat con {name}",
			resize: "Arrastra para ajustar el ancho de la conversación",
			tipTitle: "{name} está aquí",
			tipBody:
				"Toca o haz clic en la esfera resaltada de la barra superior para abrir Ben. Otro clic cierra el chat. Tu conversación se conserva.",
			tipShortcut:
				"Con teclado: Ctrl + J (Windows/Linux) o ⌘ + J (Mac) abre o cierra Ben cuando no estás escribiendo en un campo.",
			tipDone: "¡Eso es! Cuando necesites ayuda, solo haz clic.",
			tipDismiss: "Entendido",
		},
		attach: {
			file: "archivo",
			openFile: "Abrir {name}",
			open: "abrir",
			download: "descargar",
			noPort: "Este enlace no abre archivos.",
			downloadFailed: "No se pudo descargar ese archivo.",
			openFailed: "No se pudo abrir ese archivo.",
			badType: "{name}: Ben lee {formats}.",
			tooBig: "{name}: archivo demasiado grande ({max} MB como máximo).",
			uploadFailed: "{name}: el envío falló — inténtalo de nuevo.",
			tooMany: "Hasta {max} archivos por mensaje.",
			uploading: "enviando…",
			unreadable: "no se pudo leer la imagen",
		},
		rating: {
			up: "la respuesta ayudó",
			down: "la respuesta no ayudó",
			seeTurn: "ver el turno",
			whatWasWrong: "¿qué estaba mal? (opcional)",
			whatWasWrongLabel: "Qué estaba mal",
			send: "enviar",
		},
		recovery: {
			checking: "La conexión se cayó — comprobando si llegó la respuesta…",
			arrived: "La conexión se cayó a medias, pero la respuesta llegó.",
			lost: "La conexión se cayó. Aun así la respuesta puede haberse generado y guardado — mira el turno antes de enviarlo de nuevo.",
			seeTurn: "ver el turno",
		},
		error: {
			expired: "tu sesión ha caducado — inicia sesión de nuevo",
			couldNotStart: "no se pudo iniciar ({status})",
			somethingWentWrong: "algo salió mal",
			forbidden: "Solo un administrador puede hacer eso.",
			unreachable: "No se pudo contactar con el servidor.",
			generic: "No se pudo terminar.",
		},
		...agentToolMessages.es,
	},
	fr: {
		workbench: {
			settingsTab: "Réglages",
			knowledgeTab: "Connaissances",
			chatTab: "Conversation",
			testTab: "Tester l’agent",
			on: "activé",
			off: "désactivé",
			turnOn: "Activer",
			turnOff: "Désactiver",
			attendant: {
				title: "Celui-ci est un conseiller : il parle à vos clients.",
				why: "Il ne répond pas ici dans le panneau — c'est la règle qui empêche quelqu'un de l'extérieur de lui demander de supprimer une table. Pour l'essayer, ouvrez le chat comme le ferait un visiteur.",
				test: "L'essayer en tant que client",
				noChat:
					"Vous n'avez pas encore de chat publié sur le site. Créez un canal {webchat} dans Canaux — pas besoin de numéro vérifié ni d'application approuvée — et publiez-le. Cela prend une minute et c'est le moyen le plus rapide de voir cet agent au travail.",
				webchat: "webchat",
			},
		},
		resources: {
			heading: "Ressources",
			files: "Créer un document",
			media: "Créer une image",
			web: "Rechercher sur le web",
			filesRequest: "Créez un document sur : ",
			mediaRequest: "Créez une image de : ",
			webRequest: "Recherchez sur le web : ",
		},
		document: {
			saveFailed: "Impossible d’enregistrer. Votre brouillon est conservé.",
			actions: "Actions du document",
			openTab: "Ouvrir dans un nouvel onglet",
			openPdf: "Ouvrir le PDF",
			tableLimit: "Aperçu limité à 200 lignes et 100 colonnes. Téléchargez le fichier complet.",
			edit: "Modifier le document",
			save: "Enregistrer une nouvelle version",
			cancel: "Annuler",
			compare: "Comparer les versions",
			conflict:
				"Le fichier a changé. Copiez vos modifications, fermez et ouvrez la dernière version.",

			versions: "Versions",
			version: "Version {version}",
			restore: "Demander la restauration",
			revise: "Modifier la sélection",
			reviseRequest:
				"Dans le document {name}, modifiez cet extrait :\n« {text} »\nModification souhaitée : ",
			restoreRequest: "Restaurez la version {version} du fichier {name}.",

			heading: "Document",
			close: "Revenir à la conversation",
			preview: "Aperçu",
			source: "Source",
			writing: "Rédaction…",
			incomplete: "Aperçu incomplet",
			loading: "Chargement…",
			failed: "Impossible de prévisualiser le fichier. Vous pouvez le télécharger.",
			open: "Ouvrir l’aperçu",
		},
		role: {
			attendant: "conseiller",
		},
		sidebar: {
			search: "Rechercher un agent",
			attendants: "Conseillers",
			attendantsHint: "Ils parlent à vos clients, sur les canaux.",
			system: "Le système",
			systemHint: "Ben travaille avec votre équipe dans tout le système.",
			answers: "Répond sur : {names}",
			off: "désactivé",
			noMatch: "Aucun agent de ce nom.",
			newAttendant: "+ Nouveau conseiller",
		},
		settings: {
			outbound: {
				title: "Messages aux clients",
				lead: "S'applique à tout ce que cet agent dit aux clients — ses réponses dans une conversation et quand {thisAgent}.",
				leadDecides: "cet agent décide d'écrire à quelqu'un",
				sendLabel: "Envoie tout seul",
				sendHint:
					"Le fonctionnement actuel. Le message part aussitôt et apparaît dans la conversation.",
				askLabel: "Attend une approbation",
				askHint:
					"Le message reste dans la conversation en brouillon, avec Envoyer, Modifier et Refuser. Rien ne part tant que personne ne clique.",
			},
			name: {
				title: "Nom",
				lead: "Comment il s'appelle. C'est le nom qui apparaît dans le sélecteur de canal et dans les conversations — son rôle ({role}) ne change pas avec lui.",
				label: "Nom de l'agent",
			},
			tier: {
				title: "Modèle d'IA",
				fastHint: "réponses courtes et tâches simples",
				smartHint: "opération au quotidien",
				premiumHint: "construire la structure, décisions difficiles",
				fast: "Rapide",
				smart: "Équilibré",
				premium: "Plus performant",
			},
			sex: {
				title: "Sexe",
				hint: "Choisit la voix avec laquelle il parle, et le visage qu'il reçoit.",
				male: "Masculin",
				female: "Féminin",
				neutral: "Neutre",
				none: "Non précisé",
			},
			copy: {
				nameSuffix: "{name} (copie)",
				lead: "La copie naît {off} : elle hérite du persona et des outils de cet agent, et rien d'elle n'atteint personne tant que vous ne l'activez pas.",
				leadOff: "désactivée",
				namePlaceholder: "Nom de la copie",
				nameLabel: "Nom de la copie",
				codeLabel: "Code de la copie",
				action: "Dupliquer",
			},
			remove: {
				lead: "Supprimer {name} n'efface pas l'historique : tout ce qu'il a répondu reste dans Consommation. Ce qui disparaît, c'est la configuration — persona, outils et le code {code}, que des automatisations et des canaux utilisent peut-être.",
				confirm: "Supprimer quand même",
				action: "Supprimer",
			},
			discard: "Abandonner",
		},
		persona: {
			title: "Qui il est",
			filled: "{n} sur 5 remplies",
			missingEscalation: "· il reste à dire quand appeler une personne",
			lead: "Ceci S'AJOUTE aux instructions du rôle — cela ne les remplace jamais, donc rien ici ne désactive les règles de sécurité. Ce qu'est l'entreprise et ce que personne ne peut dire, il en hérite déjà du Compte ; ne le répétez pas ici.",
			objective: {
				label: "À quoi il sert",
				hint: "Sa tâche, en une phrase. C'est ce qu'il essaie d'accomplir dans chaque conversation.",
				placeholder:
					"Résoudre les questions de ceux qui ont déjà acheté — commande, livraison et échange — sans avoir à passer la main.",
			},
			tone: {
				label: "Comment il parle",
				hint: "La manière, pas le contenu : proche ou formel, court ou détaillé.",
				placeholder:
					"Direct et cordial. Phrases courtes, sans jargon. Appelle la personne par son prénom et confirme ce qu'il a compris avant d'agir.",
			},
			guardrails: {
				label: "Ce qu'il ne fait jamais",
				hint: "Ne vaut que pour CET agent. Ce que personne dans l'entreprise ne peut dire se trouve dans Paramètres → Espace de travail → L'activité (Règles fixes), et s'applique déjà ici.",
				placeholder:
					"Ne promet jamais une date de livraison sans vérifier dans le système. N'offre jamais de remise. Ne demande jamais de données de carte.",
			},
			knowledgeScope: {
				label: "De quoi il parle",
				hint: "Son sujet. En dehors, il n'invente rien : il vérifie dans le système ou demande de l'aide à l'équipe.",
				placeholder:
					"Commandes, délais, échanges et retours. Ne parle pas de contrats, du prix d'un nouveau produit, ni de questions juridiques.",
			},
			escalation: {
				label: "Quand il appelle une personne",
				hint: "Le signal d'arrêter et de demander de l'aide. Sans lui, il insiste seul précisément là où insister est l'erreur.",
				placeholder:
					"Quand quelqu'un demande une annulation, quand il évoque une réclamation formelle, ou quand la même question revient une troisième fois.",
			},
			freeText: {
				open: "Autres instructions",
				closed: "+ Autres instructions",
				lead: "Texte libre, envoyé après les cinq champs ci-dessus. C'est là que vit ce qui était déjà écrit ici avant eux.",
				label: "Texte libre du persona",
			},
			preview: "Voir le texte qu'il reçoit",
		},
		create: {
			titleAttendant: "Nouveau conseiller",
			titleTeam: "Nouvel agent pour l'équipe",
			leadAttendant:
				"Un conseiller répond à vos clients sur les canaux — WhatsApp, site, Instagram.",
			leadTeam:
				"Il travaille ici, dans le panneau, avec vous et l'équipe — il n'apparaît sur aucun canal.",
			nameLabel: "Nom",
			namePlaceholder: "Après-vente",
			codeHint:
				"Adresse fixe : {code} — automatisations et canaux pointent dessus et elle ne change plus ensuite.",
			advanced: "Avancé : créer un agent pour l'équipe, pas un conseiller",
			hideAdvanced: "Masquer — je veux seulement un conseiller",
			whoItTalksTo: "À qui il parle",
			modeInteraction: "À vos clients",
			modeInteractionHint:
				"Répond sur les canaux. C'est lui qui répond sur WhatsApp, sur le site, sur Instagram.",
			modeOperator: "À votre équipe",
			modeOperatorHint:
				"Travaille avec votre équipe : agit, crée des écrans et automatise avec vos autorisations.",
			objectiveLabel: "À quoi il sert",
			objectiveHint:
				"Le ton, les limites et le moment d'appeler une personne se règlent ensuite, dans Régler cet agent.",
		},
		/**
		 * O QUE UM AGENTE SABE, em três títulos — lido de
		 * `/v3/agents/:code/capabilities`, pelas mesmas funções que o turno usa,
		 * para o painel nunca prometer o que o turno não oferece.
		 *
		 * `artifacts` é SABE FAZER: as funções do sistema ao alcance dele, com um
		 * interruptor por agente. `sees` é SABE SOBRE: o que lhe põem na mão antes
		 * de responder. `tools` é SEMPRE SABE: as mãos que vêm com o modo, que
		 * ninguém liga aqui — `ToolMatrix`, logo abaixo, é que é o editor.
		 */
		capabilities: {
			title: "Ce que {name} sait",
			sees: {
				title: "Connaît",
				lead: "Ce qu'on lui met en main avant qu'il réponde.",
				businessProfile: "fiche de l'entreprise",
				brandRules: "règles de marque",
				persona: "persona",
				memories: "{n} souvenir | {n} souvenirs",
				documents: "{n} document | {n} documents",
				fixBusiness: "à remplir sous Compte",
				fixBrand: "à écrire sous Compte",
				fixPersona: "à répondre ci-dessous",
				fixDocuments: "à déposer sous Mémoire",
			},
			artifacts: {
				title: "Sait faire",
				lead: "Les fonctions de votre système. En activer une laisse cet agent l'exécuter seul.",
				empty: "aucune fonction n'est encore en service",
				inputs: "Reçoit : {list}",
				optional: "{name} (facultatif)",
				byMode: "parce qu'il est {mode}",
				byAutomation: "lancée par l'automatisation",
				teamOnly: "équipe seulement",
				switchLabel: "laisser {name} exécuter {artifact}",
				error: "Impossible de changer qui peut l'exécuter.",
			},
			tools: {
				title: "Sait toujours",
				lead: "Ce qu'il fait seul, dans chaque conversation.",
				count: "{n} outil | {n} outils",
			},
			error: "Impossible de lire ce que sait cet agent.",
		},
		tools: {
			title: "Outils",
			count: "({on} sur {total})",
			lead: "Seulement les outils que le mode {mode} peut voir. En désactiver un revient à ce qu'il n'existe pas pour cet agent.",
			allOn: "Tous activés — rien n'a encore été restreint.",
			technical: "Détails techniques",
		},
		authority: {
			title: "Autorité",
			hint: "Un agent a un rôle, comme une personne. L'accès ne dépasse jamais le rôle. Ce qu'il voit se définit dans Paramètres → Accès, pour les personnes comme pour les agents.",
			role: "S'exécute comme {role}",
			changeIn: "Changer dans Paramètres → Accès",
		},
		chat: {
			composePlaceholder: "Écrivez un message…",
			placeholder: "Écrivez…  Entrée envoie, Maj+Entrée passe à la ligne",
			answering: "répond…",
			stop: "Arrêter",
			queue: "Mettre à la suite",
			queued: "Ensuite : {text}",
			attach: "Joindre un fichier",
			attachHint:
				"Joindre un fichier (image, vidéo, audio, PDF, tableur) — ou collez simplement (Ctrl+V) et glissez",
			record: "Enregistrer une note vocale",
			stopRecording: "Terminer l'enregistrement",
			cancelRecording: "Abandonner l'enregistrement",
			voiceNote: "🎙️ Note vocale",
			removeAudio: "Retirer la note vocale",
			noMicrophone: "Impossible d'accéder au microphone.",
			removeAttached: "Retirer {name}",
			sentImage: "image jointe",
			editQuestion: "Corriger la question",
			cancelEdit: "annuler",
			resend: "renvoyer",
			editAndResend: "modifier et renvoyer",
			silent: "L'agent a choisi de ne pas répondre cette fois.",
			fromAgent: "Résultat d’un agent en arrière-plan",
			refused:
				"Le fournisseur du modèle a refusé ce message. Il reste hors du contexte de cette conversation — reformulez la question.",
			longThread:
				"Conversation longue — chaque réponse relit tout. En démarrer une nouvelle coûte moins cher et va plus vite.",
			toEnd: "↓ aller à la fin",
			showMore: "Afficher plus",
			showLess: "Afficher moins",
			copy: "copier",
			copied: "copié",
			useAsReply: "utiliser comme réponse",
			regenerate: "régénérer",
			deleteMessage: "supprimer le message",
			confirmDelete: "vraiment supprimer ?",
			tokens: "· {n} tokens",
			whatIsThis: "Qu'est-ce que c'est ?",
		},
		suggestions: {
			hello: "Bonjour 👋",
			toStart: "Pour commencer",
			orWrite: "Touchez une suggestion ou écrivez ce dont vous avez besoin.",
			orWriteAlone: "Écrivez une question, une demande ou une commande pour commencer.",
			manage: {
				title: "Questions suggérées",
				lead: "Ce que l'assistant propose lorsqu'il est ouvert sur chaque écran. Écrites à partir de votre activité, et à vous de les modifier.",
				generate: "Les écrire à partir de mon activité",
				generating: "écriture…",
				generated: "{n} questions écrites.",
				unavailable: "Aucun modèle n'a répondu. Rien n'a été modifié.",
				unreadable: "La réponse n'a pas pu être lue. Rien n'a été modifié.",
				failed: "Impossible de les écrire : {reason}. Rien n'a été modifié.",
				anyScreen: "N'importe quel écran",
				gone: "Un écran qui n'est plus dans le menu",
				add: "Nouvelle question",
				questionLabel: "La question, dans les mots de celui qui la poserait",
				shortLabel: "Texte court du bouton (facultatif)",
				save: "Enregistrer",
				cancel: "Annuler",
				remove: "Supprimer",
				turnOn: "activer",
				turnOff: "désactiver",
				off: "désactivée",
				byModel: "écrite par le modèle",
				empty:
					"Rien d'écrit pour l'instant. Demandez-les à partir de votre activité, ou écrivez la première.",
				/** As de fábrica — ver o bloco pt-BR para o porquê do verbo. */
				factoryHint:
					"Voici celles d'usine : ce que Ben propose sur chaque écran tant que cet espace n'en a écrit aucune.",
				adopt: "En garder une copie à moi",
				screenPick: "Écran",
			},
			hintInConversation:
				"Demandez-lui quoi répondre. Il lit la mémoire de l'espace de travail et ne parle pas au client.",
			hintInternal: "Demandez à Ben de travailler, créer des écrans et automatiser vos tâches.",
			panel: {
				/**
				 * A PRIMEIRA das três, por decisão do dono (24/09): o que um workspace
				 * recém-nascido quer do Ben é o site no ar, não um resumo do dia que
				 * ainda não teve nada dentro.
				 */
				site: "Crée le site de mon entreprise",
				/**
				 * UM GESTO NATIVO (desde 05/10 o atendente etiqueta a pessoa da
				 * conversa sem pacote nenhum, e o Ben do painel sempre etiquetou).
				 *
				 * Escrita da boca de QUEM PEDE, como todas as outras: o que se toca
				 * aqui é um pedido ao Ben, não uma frase dele.
				 */
				tagging: "Étiquette les conversations pour moi",
				/** Os atalhos do "/" no compositor: a tela com papel `composer.snippet`. */
				snippets: "Crée les réponses rapides du support",
				screen: "Crée un écran de devis",
				/**
				 * A pergunta de quem JÁ TEM MOVIMENTO — e a última da oferta, por
				 * isso: numa casa recém-aberta não há registro para estar parado.
				 */
				stalled: "Quelles fiches sont bloquées depuis plus d'une semaine ?",
				/**
				 * NÃO está na oferta, e é usada como CONTRA-EXEMPLO: ela é a pergunta
				 * certa no painel e a errada com um cliente à espera (`ben-dock.spec`).
				 */
				today: "Qu'est-ce qui a changé aujourd'hui ?",
			},
			attendant: {
				discount: "Comment répondrais-tu à une demande de remise ?",
			},
			inConversation: {
				reply: "Suggère une réponse",
				summarise: "Résume cette conversation en trois lignes",
				known: "Que sait-on déjà de ce client ?",
			},
		},
		missionLive: {
			main: "Ben",
			thinking: "Réflexion",
			reading: "Lecture de {site}",
			readingPage: "Lecture d’une page",
			/** `change_system` numa missão é sempre um PLANO: ninguém ao volante aplica (QA-007). */
			drafting: "Rédaction de la proposition",
			crew: {
				title: "{n} parties de la proposition rédigées en même temps",
				done: "{n} prêtes",
				running: "{n} en cours",
				queued: "{n} en file",
				chars: "{kb} Ko écrits",
				planning: "planifie",
			},
			status: { queued: "en file", running: "écrit", succeeded: "terminé", failed: "échec" },
		},
		live: {
			acting: "en action…",
			writing: "écrit {tool} — {kb} KB…",
			writingLive: "en direct",
			activity: "Activité",
			reasoningRaw: "raisonnement (dev)",
			tasks: "{n} étapes, {running} en cours",
			credits: "{n} cr",
			expand: "ouvrir",
			collapse: "fermer",
			awaitsHuman: "En attente de la réponse d'une personne.",
			running: "en cours",
			thinking: "réfléchit…",
			reasoning: "raisonnement",
			closeOutput: "fermer",
			seeOutput: "voir le résultat",
		},
		steps: {
			one: "outil",
			many: "outils",
			input: "entrée",
			output: "résultat",
			running: "en cours…",
			fields: "{n} champs",
		},
		approval: {
			approve: "Approuver",
			done: "Approuvé — les agents l'utilisent déjà.",
			discarded: "Cette proposition a été écartée.",
			failed: "Impossible d'approuver. Réessayez ou passez par Mémoire → Ce que l'équipe a appris.",
			replaces: "remplace la ligne actuelle",
			toLibrary: "va dans la Bibliothèque",
			forAgent: "seulement pour {agent}",
			title: {
				generic: "En attente de votre approbation",
				knowledge: "Connaissance pour les agents",
			},
		},
		navigate: {
			contact: "contact",
			conversation: "conversation",
			record: "fiche",
			screen: "écran",
			back: "retour",
			open: "ouvrir",
			toolFile: "fichier renvoyé par l'outil",
		},
		sessions: {
			searchFailed: "La recherche a échoué. Réessayez.",
			filter: "Filtrer les conversations",
			empty: "Aucune conversation pour l'instant.",
			noMatch: "Rien avec « {term} ».",
			aboutConversation: "À propos d'une conversation",
			conversationBadge: "conversation",
			untitled: "Sans titre",
			messages: "{n} messages",
			removeLabel: "Supprimer {title}",
			thisConversation: "cette conversation",
			confirmRemove: "confirmer",
			remove: "supprimer",
			/** O cabeçalho do painel de conversas quando ele ocupa a doca inteira. */
			heading: "Conversations précédentes",
			/** Os grupos por data, no fuso do workspace. */
			groups: {
				today: "Aujourd'hui",
				yesterday: "Hier",
				week: "7 derniers jours",
				older: "Plus anciennes",
			},
			ago: {
				now: "maintenant",
				minutes: "{n} min",
				hours: "{n} h",
				days: "{n} j",
			},
		},
		dock: {
			purposeInConversation: "vous seul le voyez — rien d'ici n'est envoyé",
			confirmRestart: "Recommencer ? Cliquez encore",
			noBen:
				"Cet espace de travail n'a aucun assistant de panneau activé. Activez-en un dans Agents pour demander de l'aide ici même.",
			purposeInternal: "travaillez, construisez et automatisez",
			acting: "utilise un outil…",
			typing: "écrit…",
			thinking: "réfléchit…",
			whichAgent: "À quel agent parler",
			conversations: "Conversations",
			newConversation: "Nouvelle conversation",
			wide: "plein écran",
			narrow: "revenir sur le côté",
			close: "Fermer",
			/** O botão de histórico com o painel aberto: o clique seguinte devolve o chat. */
			backToChat: "Retour à la conversation",
			hideHistory: "Masquer les conversations",
			copilot: "Ben · Copilote",
			launcher: "Discuter avec {name}",
			resize: "Glissez pour ajuster la largeur de la conversation",
			tipTitle: "{name} est là",
			tipBody:
				"Touchez ou cliquez sur l’orbe en surbrillance dans la barre du haut pour ouvrir Ben. Un autre clic ferme le chat. Votre conversation est conservée.",
			tipShortcut:
				"Au clavier : Ctrl + J (Windows/Linux) ou ⌘ + J (Mac) ouvre ou ferme Ben lorsque vous ne saisissez pas de texte dans un champ.",
			tipDone: "C’est ça ! Dès que vous avez besoin d’aide, un clic suffit.",
			tipDismiss: "Compris",
		},
		attach: {
			file: "fichier",
			openFile: "Ouvrir {name}",
			open: "ouvrir",
			download: "télécharger",
			noPort: "Ce lien n'ouvre pas de fichiers.",
			downloadFailed: "Impossible de télécharger ce fichier.",
			openFailed: "Impossible d'ouvrir ce fichier.",
			badType: "{name} : Ben lit {formats}.",
			tooBig: "{name} : fichier trop volumineux ({max} Mo maximum).",
			uploadFailed: "{name} : l'envoi a échoué — réessayez.",
			tooMany: "Jusqu’à {max} fichiers par message.",
			uploading: "envoi…",
			unreadable: "impossible de lire l'image",
		},
		rating: {
			up: "la réponse a aidé",
			down: "la réponse n'a pas aidé",
			seeTurn: "voir l'échange",
			whatWasWrong: "qu'est-ce qui n'allait pas ? (facultatif)",
			whatWasWrongLabel: "Ce qui n'allait pas",
			send: "envoyer",
		},
		recovery: {
			checking: "La connexion a lâché — vérification que la réponse est bien arrivée…",
			arrived: "La connexion a lâché en cours de route, mais la réponse est arrivée.",
			lost: "La connexion a lâché. La réponse a tout de même pu être produite et archivée — regardez l'échange avant de renvoyer.",
			seeTurn: "voir l'échange",
		},
		error: {
			expired: "votre session a expiré — reconnectez-vous",
			couldNotStart: "impossible de démarrer ({status})",
			somethingWentWrong: "quelque chose a mal tourné",
			forbidden: "Seul un administrateur peut faire cela.",
			unreachable: "Impossible de joindre le serveur.",
			generic: "Impossible de terminer.",
		},
		...agentToolMessages.fr,
	},
	it: {
		workbench: {
			settingsTab: "Impostazioni",
			knowledgeTab: "Conoscenza",
			chatTab: "Conversazione",
			testTab: "Prova agente",
			on: "attivo",
			off: "spento",
			turnOn: "Attiva",
			turnOff: "Spegni",
			attendant: {
				title: "Questo è un operatore: parla con i tuoi clienti.",
				why: "Non risponde qui nel pannello — è la regola che impedisce a qualcuno da fuori di chiedergli di cancellare una tabella. Per provarlo, apri la chat come farebbe un visitatore.",
				test: "Provalo come cliente",
				noChat:
					"Non hai ancora una chat pubblicata sul sito. Crea un canale {webchat} in Canali — non serve un numero verificato né un'app approvata — e pubblicalo. Ci vuole un minuto ed è il modo più rapido per vedere questo agente al lavoro.",
				webchat: "chat del sito",
			},
		},
		resources: {
			heading: "Risorse",
			files: "Crea documento",
			media: "Crea immagine",
			web: "Cerca sul web",
			filesRequest: "Crea un documento su: ",
			mediaRequest: "Crea un’immagine di: ",
			webRequest: "Cerca sul web: ",
		},
		document: {
			saveFailed: "Impossibile salvare. La bozza è ancora qui.",
			actions: "Azioni del documento",
			openTab: "Apri in una nuova scheda",
			openPdf: "Apri PDF",
			tableLimit: "Anteprima limitata a 200 righe e 100 colonne. Scarica il file completo.",
			edit: "Modifica documento",
			save: "Salva nuova versione",
			cancel: "Annulla",
			compare: "Confronta versioni",
			conflict: "Il file è cambiato. Copia le modifiche, chiudi e riapri la versione più recente.",

			versions: "Versioni",
			version: "Versione {version}",
			restore: "Chiedi ripristino",
			revise: "Modifica selezione",
			reviseRequest:
				"Nel documento {name}, modifica questo estratto:\n“{text}”\nModifica richiesta: ",
			restoreRequest: "Ripristina la versione {version} del file {name}.",

			heading: "Documento",
			close: "Torna alla conversazione",
			preview: "Anteprima",
			source: "Sorgente",
			writing: "Scrittura…",
			incomplete: "Anteprima incompleta",
			loading: "Caricamento…",
			failed: "Impossibile visualizzare il file. Puoi scaricarlo.",
			open: "Apri anteprima",
		},
		role: {
			attendant: "operatore",
		},
		sidebar: {
			search: "Cerca agente",
			attendants: "Operatori",
			attendantsHint: "Parlano con i tuoi clienti, sui canali.",
			system: "Il sistema",
			systemHint: "Ben lavora con il tuo team in tutto il sistema.",
			answers: "Risponde su: {names}",
			off: "spento",
			noMatch: "Nessun agente con questo nome.",
			newAttendant: "+ Nuovo operatore",
		},
		settings: {
			outbound: {
				title: "Messaggi ai clienti",
				lead: "Vale per tutto ciò che questo agente dice ai clienti — le sue risposte dentro la conversazione e quando {thisAgent}.",
				leadDecides: "questo agente decide di scrivere a qualcuno",
				sendLabel: "Invia da solo",
				sendHint: "Come funziona oggi. Il messaggio parte subito e compare nella conversazione.",
				askLabel: "Aspetta un'approvazione",
				askHint:
					"Il messaggio resta nella conversazione come bozza, con Invia, Modifica e Rifiuta. Non parte nulla finché qualcuno non clicca.",
			},
			name: {
				title: "Nome",
				lead: "Come si chiama. È il nome che compare nel selettore di canale e nelle conversazioni — il suo ruolo ({role}) non cambia con questo.",
				label: "Nome dell'agente",
			},
			tier: {
				title: "Modello IA",
				fastHint: "risposte brevi e compiti semplici",
				smartHint: "operatività quotidiana",
				premiumHint: "costruire struttura, decisioni difficili",
				fast: "Veloce",
				smart: "Bilanciato",
				premium: "Più capace",
			},
			sex: {
				title: "Sesso",
				hint: "Sceglie la voce con cui parla, e il volto che riceve.",
				male: "Maschile",
				female: "Femminile",
				neutral: "Neutro",
				none: "Non detto",
			},
			copy: {
				nameSuffix: "{name} (copia)",
				lead: "La copia nasce {off}: eredita la persona e gli strumenti di questo agente, e niente di lei arriva a nessuno finché non la accendi.",
				leadOff: "spenta",
				namePlaceholder: "Nome della copia",
				nameLabel: "Nome della copia",
				codeLabel: "Codice della copia",
				action: "Duplica",
			},
			remove: {
				lead: "Eliminare {name} non cancella lo storico: tutto quello che ha risposto resta in Consumo. Quello che sparisce è la configurazione — persona, strumenti e il codice {code}, che automazioni e canali potrebbero star usando.",
				confirm: "Elimina comunque",
				action: "Elimina",
			},
			discard: "Scarta",
		},
		persona: {
			title: "Chi è",
			filled: "{n} di 5 compilate",
			missingEscalation: "· manca ancora dire quando chiamare una persona",
			lead: "Questo si AGGIUNGE alle istruzioni del ruolo — non le sostituisce mai, quindi niente qui spegne le regole di sicurezza. Che cos'è l'azienda e cosa nessuno può dire lo eredita già dall'Account; non ripeterlo qui.",
			objective: {
				label: "A cosa serve",
				hint: "Il suo compito, in una frase. È quello che prova a portare a termine in ogni conversazione.",
				placeholder:
					"Risolvere i dubbi di chi ha già comprato — ordine, consegna e cambio — senza doverli passare a qualcun altro.",
			},
			tone: {
				label: "Come parla",
				hint: "Il modo, non il contenuto: vicino o formale, breve o dettagliato.",
				placeholder:
					"Diretto e cordiale. Frasi brevi, senza gergo. Chiama la persona per nome e conferma quello che ha capito prima di agire.",
			},
			guardrails: {
				label: "Cosa non fa mai",
				hint: "Vale solo per QUESTO agente. Cosa nessuno in azienda può dire sta in Impostazioni → Spazio di lavoro → L'attività (Regole fisse), e vale già qui.",
				placeholder:
					"Non promette mai una data di consegna senza controllare nel sistema. Non offre mai sconti. Non chiede mai i dati della carta.",
			},
			knowledgeScope: {
				label: "Di cosa parla",
				hint: "Il suo argomento. Fuori da lì non inventa nulla: verifica nel sistema o chiede aiuto al team.",
				placeholder:
					"Ordini, tempi, cambi e resi. Non parla di contratti, del prezzo di un prodotto nuovo né di questioni legali.",
			},
			escalation: {
				label: "Quando chiama una persona",
				hint: "Il segnale per fermarsi e chiedere aiuto. Senza, insiste da solo proprio nel caso in cui insistere è l'errore.",
				placeholder:
					"Quando qualcuno chiede di annullare, quando parla di un reclamo formale, o quando la stessa domanda torna per la terza volta.",
			},
			freeText: {
				open: "Altre istruzioni",
				closed: "+ Altre istruzioni",
				lead: "Testo libero, inviato dopo i cinque campi qui sopra. È dove sta quello che era già scritto qui prima di loro.",
				label: "Testo libero della persona",
			},
			preview: "Vedi il testo che riceve",
		},
		create: {
			titleAttendant: "Nuovo operatore",
			titleTeam: "Nuovo agente per il team",
			leadAttendant:
				"Un operatore risponde ai tuoi clienti sui canali — WhatsApp, sito, Instagram.",
			leadTeam:
				"Lavora qui dentro, nel pannello, con te e con il team — non compare su nessun canale.",
			nameLabel: "Nome",
			namePlaceholder: "Post-vendita",
			codeHint: "Indirizzo fisso: {code} — automazioni e canali puntano lì e non cambia più.",
			advanced: "Avanzate: crea un agente per il team, non un operatore",
			hideAdvanced: "Nascondi — voglio solo un operatore",
			whoItTalksTo: "Con chi parla",
			modeInteraction: "Con i tuoi clienti",
			modeInteractionHint:
				"Risponde sui canali. È questo che risponde su WhatsApp, sul sito, su Instagram.",
			modeOperator: "Con il tuo team",
			modeOperatorHint:
				"Lavora con il tuo team: opera, crea schermate e automatizza con le tue autorizzazioni.",
			objectiveLabel: "A cosa serve",
			objectiveHint:
				"Il tono, i limiti e quando chiamare una persona li regoli dopo, in Regola questo agente.",
		},
		/**
		 * O QUE UM AGENTE SABE, em três títulos — lido de
		 * `/v3/agents/:code/capabilities`, pelas mesmas funções que o turno usa,
		 * para o painel nunca prometer o que o turno não oferece.
		 *
		 * `artifacts` é SABE FAZER: as funções do sistema ao alcance dele, com um
		 * interruptor por agente. `sees` é SABE SOBRE: o que lhe põem na mão antes
		 * de responder. `tools` é SEMPRE SABE: as mãos que vêm com o modo, que
		 * ninguém liga aqui — `ToolMatrix`, logo abaixo, é que é o editor.
		 */
		capabilities: {
			title: "Che cosa sa {name}",
			sees: {
				title: "Conosce",
				lead: "Quello che gli mettono in mano prima che risponda.",
				businessProfile: "scheda dell'attività",
				brandRules: "regole del marchio",
				persona: "persona",
				memories: "{n} memoria | {n} memorie",
				documents: "{n} documento | {n} documenti",
				fixBusiness: "compilarla in Account",
				fixBrand: "scriverle in Account",
				fixPersona: "rispondere qui sotto",
				fixDocuments: "caricare in Memoria",
			},
			artifacts: {
				title: "Sa fare",
				lead: "Le funzioni del tuo sistema. Accenderne una lascia che questo agente la esegua da solo.",
				empty: "nessuna funzione è ancora in servizio",
				inputs: "Riceve: {list}",
				optional: "{name} (facoltativo)",
				byMode: "perché è {mode}",
				byAutomation: "avviata dall'automazione",
				teamOnly: "solo il team",
				switchLabel: "lasciare che {name} esegua {artifact}",
				error: "Non è stato possibile cambiare chi può eseguirla.",
			},
			tools: {
				title: "Sa sempre",
				lead: "Quello che fa da solo, in ogni conversazione.",
				count: "{n} strumento | {n} strumenti",
			},
			error: "Non è stato possibile leggere cosa sa questo agente.",
		},
		tools: {
			title: "Strumenti",
			count: "({on} di {total})",
			lead: "Solo gli strumenti che la modalità {mode} vede. Spegnerne uno equivale a non farlo esistere per questo agente.",
			allOn: "Tutti accesi — non è ancora stato limitato niente.",
			technical: "Dettagli tecnici",
		},
		authority: {
			title: "Autorità",
			hint: "Un agente ha un ruolo, come una persona. L'accesso non supera mai il ruolo. Ciò che vede si definisce in Impostazioni → Accessi, per persone e agenti allo stesso modo.",
			role: "Gira come {role}",
			changeIn: "Cambia in Impostazioni → Accessi",
		},
		chat: {
			composePlaceholder: "Scrivi un messaggio…",
			placeholder: "Scrivi…  Invio manda, Maiusc+Invio va a capo",
			answering: "sta rispondendo…",
			stop: "Ferma",
			queue: "Accoda",
			queued: "Poi: {text}",
			attach: "Allega un file",
			attachHint:
				"Allega un file (immagine, video, audio, PDF, foglio di calcolo) — o incolla soltanto (Ctrl+V) e trascina",
			record: "Registra una nota vocale",
			stopRecording: "Concludi la registrazione",
			cancelRecording: "Scarta la registrazione",
			voiceNote: "🎙️ Nota vocale",
			removeAudio: "Rimuovi la nota vocale",
			noMicrophone: "Non è stato possibile accedere al microfono.",
			removeAttached: "Rimuovi {name}",
			sentImage: "immagine allegata",
			editQuestion: "Correggi la domanda",
			cancelEdit: "annulla",
			resend: "reinvia",
			editAndResend: "modifica e reinvia",
			silent: "L'agente ha scelto di non rispondere questa volta.",
			fromAgent: "Risultato di un agente in background",
			refused:
				"Il fornitore del modello ha rifiutato questo messaggio. Resta fuori dal contesto della conversazione — chiedi in un altro modo.",
			longThread:
				"Conversazione lunga — ogni risposta rilegge tutto. Iniziarne una nuova costa meno ed è più veloce.",
			toEnd: "↓ vai alla fine",
			showMore: "Mostra di più",
			showLess: "Mostra meno",
			copy: "copia",
			copied: "copiato",
			useAsReply: "usa come risposta",
			regenerate: "rigenera",
			deleteMessage: "elimina messaggio",
			confirmDelete: "eliminare davvero?",
			tokens: "· {n} token",
			whatIsThis: "Che cos'è questo?",
		},
		suggestions: {
			hello: "Ciao 👋",
			toStart: "Per iniziare",
			orWrite: "Tocca un suggerimento o scrivi ciò che ti serve.",
			orWriteAlone: "Scrivi una domanda, una richiesta o un comando per iniziare.",
			manage: {
				title: "Domande suggerite",
				lead: "Ciò che l'assistente propone quando viene aperto su ogni schermata. Scritte a partire dalla tua attività, e tue da modificare.",
				generate: "Scrivile dalla mia attività",
				generating: "sta scrivendo…",
				generated: "{n} domande scritte.",
				unavailable: "Nessun modello ha risposto. Non è stato cambiato nulla.",
				unreadable: "La risposta non è stata leggibile. Non è stato cambiato nulla.",
				failed: "Non è stato possibile scriverle: {reason}. Non è stato cambiato nulla.",
				anyScreen: "Qualsiasi schermata",
				gone: "Una schermata che non è più nel menu",
				add: "Nuova domanda",
				questionLabel: "La domanda, con le parole di chi la farebbe",
				shortLabel: "Testo breve per il pulsante (facoltativo)",
				save: "Salva",
				cancel: "Annulla",
				remove: "Rimuovi",
				turnOn: "attiva",
				turnOff: "disattiva",
				off: "disattivata",
				byModel: "scritta dal modello",
				empty: "Ancora nulla di scritto. Chiedile a partire dalla tua attività, o scrivi la prima.",
				/** As de fábrica — ver o bloco pt-BR para o porquê do verbo. */
				factoryHint:
					"Queste sono quelle di fabbrica: ciò che Ben offre in ogni schermata finché questo spazio non ne ha scritta nessuna.",
				adopt: "Tenerne una copia mia",
				screenPick: "Schermata",
			},
			hintInConversation:
				"Chiedigli cosa rispondere. Legge la memoria dello spazio di lavoro e non parla con il cliente.",
			hintInternal: "Chiedi a Ben di operare, creare schermate e automatizzare il lavoro.",
			panel: {
				/**
				 * A PRIMEIRA das três, por decisão do dono (24/09): o que um workspace
				 * recém-nascido quer do Ben é o site no ar, não um resumo do dia que
				 * ainda não teve nada dentro.
				 */
				site: "Crea il sito della mia attività",
				/**
				 * UM GESTO NATIVO (desde 05/10 o atendente etiqueta a pessoa da
				 * conversa sem pacote nenhum, e o Ben do painel sempre etiquetou).
				 *
				 * Escrita da boca de QUEM PEDE, como todas as outras: o que se toca
				 * aqui é um pedido ao Ben, não uma frase dele.
				 */
				tagging: "Etichetta le conversazioni al posto mio",
				/** Os atalhos do "/" no compositor: a tela com papel `composer.snippet`. */
				snippets: "Crea le risposte rapide dell'assistenza",
				screen: "Crea una schermata dei preventivi",
				/**
				 * A pergunta de quem JÁ TEM MOVIMENTO — e a última da oferta, por
				 * isso: numa casa recém-aberta não há registro para estar parado.
				 */
				stalled: "Quali record sono fermi da più di una settimana?",
				/**
				 * NÃO está na oferta, e é usada como CONTRA-EXEMPLO: ela é a pergunta
				 * certa no painel e a errada com um cliente à espera (`ben-dock.spec`).
				 */
				today: "Cosa è cambiato oggi?",
			},
			attendant: {
				discount: "Come risponderesti a una richiesta di sconto?",
			},
			inConversation: {
				reply: "Suggerisci una risposta",
				summarise: "Riassumi questa conversazione in tre righe",
				known: "Cosa sappiamo già di questo cliente?",
			},
		},
		missionLive: {
			main: "Ben",
			thinking: "Ragionamento",
			reading: "Lettura di {site}",
			readingPage: "Lettura di una pagina",
			/** `change_system` numa missão é sempre um PLANO: ninguém ao volante aplica (QA-007). */
			drafting: "Stesura della proposta",
			crew: {
				title: "{n} parti della proposta in bozza contemporaneamente",
				done: "{n} pronte",
				running: "{n} in corso",
				queued: "{n} in coda",
				chars: "{kb} KB scritti",
				planning: "pianifica",
			},
			status: { queued: "in coda", running: "scrive", succeeded: "fatto", failed: "fallito" },
		},
		live: {
			acting: "in azione…",
			writing: "sta scrivendo {tool} — {kb} KB…",
			writingLive: "dal vivo",
			activity: "Attività",
			reasoningRaw: "ragionamento (dev)",
			tasks: "{n} passi, {running} in corso",
			credits: "{n} cr",
			expand: "apri",
			collapse: "chiudi",
			awaitsHuman: "In attesa della risposta di una persona.",
			running: "in corso",
			thinking: "sta pensando…",
			reasoning: "ragionamento",
			closeOutput: "chiudi",
			seeOutput: "vedi il risultato",
		},
		steps: {
			one: "strumento",
			many: "strumenti",
			input: "ingresso",
			output: "risultato",
			running: "in esecuzione…",
			fields: "{n} campi",
		},
		approval: {
			approve: "Approva",
			done: "Approvato — gli agenti lo usano già.",
			discarded: "Questa proposta è stata scartata.",
			failed: "Impossibile approvare. Riprova o usa Memoria → Ciò che il team ha insegnato.",
			replaces: "sostituisce la riga attuale",
			toLibrary: "va nella Biblioteca",
			forAgent: "solo per {agent}",
			title: {
				generic: "In attesa della tua approvazione",
				knowledge: "Conoscenza per gli agenti",
			},
		},
		navigate: {
			contact: "contatto",
			conversation: "conversazione",
			record: "record",
			screen: "schermata",
			back: "indietro",
			open: "apri",
			toolFile: "file restituito dallo strumento",
		},
		sessions: {
			searchFailed: "Ricerca non riuscita. Riprova.",
			filter: "Filtra le conversazioni",
			empty: "Ancora nessuna conversazione.",
			noMatch: "Niente con “{term}”.",
			aboutConversation: "Su una conversazione",
			conversationBadge: "conversazione",
			untitled: "Senza titolo",
			messages: "{n} messaggi",
			removeLabel: "Elimina {title}",
			thisConversation: "questa conversazione",
			confirmRemove: "conferma",
			remove: "elimina",
			/** O cabeçalho do painel de conversas quando ele ocupa a doca inteira. */
			heading: "Conversazioni precedenti",
			/** Os grupos por data, no fuso do workspace. */
			groups: {
				today: "Oggi",
				yesterday: "Ieri",
				week: "Ultimi 7 giorni",
				older: "Più vecchie",
			},
			ago: {
				now: "adesso",
				minutes: "{n} min",
				hours: "{n} h",
				days: "{n} g",
			},
		},
		dock: {
			purposeInConversation: "lo vedi solo tu — niente di qui viene inviato",
			confirmRestart: "Ricominciare? Clicca di nuovo",
			noBen:
				"Questo spazio di lavoro non ha nessun assistente del pannello attivo. Attivane uno in Agenti per chiedere aiuto qui dentro.",
			purposeInternal: "opera, costruisci e automatizza",
			acting: "sta usando uno strumento…",
			typing: "sta scrivendo…",
			thinking: "sta pensando…",
			whichAgent: "Con quale agente parlare",
			conversations: "Conversazioni",
			newConversation: "Nuova conversazione",
			wide: "schermo intero",
			narrow: "torna di lato",
			close: "Chiudi",
			/** O botão de histórico com o painel aberto: o clique seguinte devolve o chat. */
			backToChat: "Torna alla conversazione",
			hideHistory: "Nascondi conversazioni",
			copilot: "Ben · Copilota",
			launcher: "Chat con {name}",
			resize: "Trascina per regolare la larghezza della conversazione",
			tipTitle: "{name} è qui",
			tipBody:
				"Tocca o clicca sulla sfera evidenziata nella barra superiore per aprire Ben. Un altro clic chiude la chat. La conversazione resta qui.",
			tipShortcut:
				"Con la tastiera: Ctrl + J (Windows/Linux) o ⌘ + J (Mac) apre o chiude Ben quando non stai scrivendo in un campo.",
			tipDone: "Esatto! Quando ti serve aiuto, basta un clic.",
			tipDismiss: "Ho capito",
		},
		attach: {
			file: "file",
			openFile: "Apri {name}",
			open: "apri",
			download: "scarica",
			noPort: "Questo link non apre file.",
			downloadFailed: "Non è stato possibile scaricare quel file.",
			openFailed: "Non è stato possibile aprire quel file.",
			badType: "{name}: Ben legge {formats}.",
			tooBig: "{name}: file troppo grande (massimo {max} MB).",
			uploadFailed: "{name}: l'invio non è riuscito — riprova.",
			tooMany: "Fino a {max} file per messaggio.",
			uploading: "invio…",
			unreadable: "non è stato possibile leggere l'immagine",
		},
		rating: {
			up: "la risposta è servita",
			down: "la risposta non è servita",
			seeTurn: "vedi il turno",
			whatWasWrong: "cosa non andava? (facoltativo)",
			whatWasWrongLabel: "Cosa non andava",
			send: "invia",
		},
		recovery: {
			checking: "La connessione è caduta — controllo se la risposta è arrivata…",
			arrived: "La connessione è caduta a metà, ma la risposta è arrivata.",
			lost: "La connessione è caduta. La risposta può essere stata comunque generata e archiviata — guarda il turno prima di inviare di nuovo.",
			seeTurn: "vedi il turno",
		},
		error: {
			expired: "la tua sessione è scaduta — accedi di nuovo",
			couldNotStart: "non è stato possibile avviare ({status})",
			somethingWentWrong: "qualcosa è andato storto",
			forbidden: "Solo un amministratore può farlo.",
			unreachable: "Non è stato possibile raggiungere il server.",
			generic: "Non è stato possibile completare.",
		},
		...agentToolMessages.it,
	},
	de: {
		workbench: {
			settingsTab: "Einstellungen",
			knowledgeTab: "Wissen",
			chatTab: "Unterhaltung",
			testTab: "Agent testen",
			on: "an",
			off: "aus",
			turnOn: "Einschalten",
			turnOff: "Ausschalten",
			attendant: {
				title: "Dieser ist ein Kundenbetreuer: Er spricht mit deinen Kunden.",
				why: "Er antwortet nicht hier im Panel — das ist die Regel, die verhindert, dass jemand von außen ihn bittet, eine Tabelle zu löschen. Zum Ausprobieren öffne den Chat so, wie ein Besucher es täte.",
				test: "Als Kunde ausprobieren",
				noChat:
					"Du hast noch keinen Chat auf der Website veröffentlicht. Lege unter Kanäle einen {webchat}-Kanal an — ohne verifizierte Nummer und ohne freigegebene App — und veröffentliche ihn. Das dauert eine Minute und ist der schnellste Weg, diesen Agenten arbeiten zu sehen.",
				webchat: "Webchat",
			},
		},
		resources: {
			heading: "Funktionen",
			files: "Dokument erstellen",
			media: "Bild erstellen",
			web: "Im Web suchen",
			filesRequest: "Erstelle ein Dokument über: ",
			mediaRequest: "Erstelle ein Bild von: ",
			webRequest: "Suche im Web nach: ",
		},
		document: {
			saveFailed: "Speichern fehlgeschlagen. Dein Entwurf bleibt erhalten.",
			actions: "Dokumentaktionen",
			openTab: "In neuem Tab öffnen",
			openPdf: "PDF öffnen",
			tableLimit:
				"Vorschau auf 200 Zeilen und 100 Spalten begrenzt. Vollständige Datei herunterladen.",
			edit: "Dokument bearbeiten",
			save: "Neue Version speichern",
			cancel: "Abbrechen",
			compare: "Versionen vergleichen",
			conflict:
				"Die Datei wurde geändert. Kopiere deine Änderungen, schließe den Editor und öffne die neueste Version.",

			versions: "Versionen",
			version: "Version {version}",
			restore: "Wiederherstellung anfragen",
			revise: "Auswahl ändern",
			reviseRequest:
				"Im Dokument {name}, ändere diesen Abschnitt:\n„{text}“\nGewünschte Änderung: ",
			restoreRequest: "Stelle Version {version} der Datei {name} wieder her.",

			heading: "Dokument",
			close: "Zurück zum Gespräch",
			preview: "Vorschau",
			source: "Quelltext",
			writing: "Wird geschrieben…",
			incomplete: "Unvollständige Vorschau",
			loading: "Vorschau wird geladen…",
			failed: "Die Vorschau ist nicht verfügbar. Die Datei kann heruntergeladen werden.",
			open: "Vorschau öffnen",
		},
		role: {
			attendant: "Kundenbetreuer",
		},
		sidebar: {
			search: "Agent suchen",
			attendants: "Kundenbetreuer",
			attendantsHint: "Sie sprechen mit deinen Kunden, auf den Kanälen.",
			system: "Das System",
			systemHint: "Ben arbeitet mit deinem Team im gesamten System.",
			answers: "Antwortet auf: {names}",
			off: "abgeschaltet",
			noMatch: "Kein Agent mit diesem Namen.",
			newAttendant: "+ Neuer Kundenbetreuer",
		},
		settings: {
			outbound: {
				title: "Nachrichten an Kunden",
				lead: "Gilt für alles, was dieser Agent Kunden schreibt — seine Antworten innerhalb einer Konversation und wenn {thisAgent}.",
				leadDecides: "dieser Agent beschließt, jemandem zu schreiben",
				sendLabel: "Sendet von selbst",
				sendHint:
					"So läuft es heute. Die Nachricht geht sofort raus und erscheint in der Konversation.",
				askLabel: "Wartet auf Freigabe",
				askHint:
					"Die Nachricht liegt als Entwurf in der Konversation, mit Senden, Bearbeiten und Ablehnen. Nichts geht raus, bis jemand klickt.",
			},
			name: {
				title: "Name",
				lead: "Wie er heißt. Es ist der Name, der in der Kanalauswahl und in den Gesprächen erscheint — seine Rolle ({role}) ändert sich dadurch nicht.",
				label: "Name des Agenten",
			},
			tier: {
				title: "Modell",
				fastHint: "kurze Antworten und einfache Aufgaben",
				smartHint: "Tagesgeschäft",
				premiumHint: "Struktur bauen, schwierige Entscheidungen",
				fast: "Schnell",
				smart: "Ausgewogen",
				premium: "Leistungsstärker",
			},
			sex: {
				title: "Geschlecht",
				hint: "Wählt die Stimme, mit der er spricht, und das Gesicht, das er bekommt.",
				male: "Männlich",
				female: "Weiblich",
				neutral: "Neutral",
				none: "Nicht gesagt",
			},
			copy: {
				nameSuffix: "{name} (Kopie)",
				lead: "Die Kopie kommt {off} zur Welt: Sie erbt Persona und Werkzeuge dieses Agenten, und nichts von ihr erreicht jemanden, bis du sie einschaltest.",
				leadOff: "abgeschaltet",
				namePlaceholder: "Name der Kopie",
				nameLabel: "Name der Kopie",
				codeLabel: "Code der Kopie",
				action: "Duplizieren",
			},
			remove: {
				lead: "{name} zu löschen löscht nicht den Verlauf: Alles, was er beantwortet hat, bleibt unter Verbrauch. Weg ist die Konfiguration — Persona, Werkzeuge und der Code {code}, den Automatisierungen und Kanäle vielleicht benutzen.",
				confirm: "Trotzdem löschen",
				action: "Löschen",
			},
			discard: "Verwerfen",
		},
		persona: {
			title: "Wer er ist",
			filled: "{n} von 5 beantwortet",
			missingEscalation: "· es fehlt noch, wann er eine Person holt",
			lead: "Das kommt ZU den Anweisungen der Rolle HINZU — es ersetzt sie nie, also schaltet hier nichts die Sicherheitsregeln ab. Was das Unternehmen ist und was niemand sagen darf, erbt er schon vom Konto; wiederhole es hier nicht.",
			objective: {
				label: "Wofür er da ist",
				hint: "Seine Aufgabe, in einem Satz. Es ist das, was er in jeder Konversation erreichen will.",
				placeholder:
					"Fragen von Leuten klären, die schon gekauft haben — Bestellung, Lieferung und Umtausch — ohne sie weiterreichen zu müssen.",
			},
			tone: {
				label: "Wie er spricht",
				hint: "Die Art, nicht der Inhalt: nah oder förmlich, kurz oder ausführlich.",
				placeholder:
					"Direkt und freundlich. Kurze Sätze, kein Fachjargon. Spricht die Person mit dem Vornamen an und bestätigt, was er verstanden hat, bevor er handelt.",
			},
			guardrails: {
				label: "Was er nie tut",
				hint: "Gilt nur für DIESEN Agenten. Was niemand im Unternehmen sagen darf, steht unter Einstellungen → Workspace → Das Geschäft (Feste Regeln) und gilt hier bereits.",
				placeholder:
					"Verspricht nie ein Lieferdatum, ohne im System nachzusehen. Bietet nie einen Rabatt an. Fragt nie nach Kartendaten.",
			},
			knowledgeScope: {
				label: "Worüber er spricht",
				hint: "Sein Thema. Außerhalb davon erfindet er nichts: Er schaut im System nach oder bittet das Team um Hilfe.",
				placeholder:
					"Bestellungen, Fristen, Umtausch und Rücksendungen. Spricht nicht über Verträge, über den Preis eines neuen Produkts oder über rechtliche Fragen.",
			},
			escalation: {
				label: "Wann er eine Person holt",
				hint: "Das Zeichen, aufzuhören und um Hilfe zu bitten. Ohne das macht er genau dort allein weiter, wo Weitermachen der Fehler ist.",
				placeholder:
					"Wenn jemand kündigen will, wenn er eine förmliche Beschwerde erwähnt, oder wenn dieselbe Frage zum dritten Mal kommt.",
			},
			freeText: {
				open: "Weitere Anweisungen",
				closed: "+ Weitere Anweisungen",
				lead: "Freier Text, der nach den fünf Feldern oben geschickt wird. Dort steht, was vor ihnen schon hier geschrieben war.",
				label: "Freier Persona-Text",
			},
			preview: "Den Text ansehen, den er bekommt",
		},
		create: {
			titleAttendant: "Neuer Kundenbetreuer",
			titleTeam: "Neuer Agent für das Team",
			leadAttendant:
				"Ein Kundenbetreuer antwortet deinen Kunden auf den Kanälen — WhatsApp, Website, Instagram.",
			leadTeam:
				"Er arbeitet hier drin, im Panel, mit dir und dem Team — er taucht auf keinem Kanal auf.",
			nameLabel: "Name",
			namePlaceholder: "After-Sales",
			codeHint:
				"Feste Adresse: {code} — Automatisierungen und Kanäle zeigen darauf, und sie ändert sich danach nicht mehr.",
			advanced: "Erweitert: einen Agenten für das Team anlegen, keinen Kundenbetreuer",
			hideAdvanced: "Ausblenden — ich will nur einen Kundenbetreuer",
			whoItTalksTo: "Mit wem er spricht",
			modeInteraction: "Mit deinen Kunden",
			modeInteractionHint:
				"Antwortet auf den Kanälen. Der hier antwortet auf WhatsApp, auf der Website, auf Instagram.",
			modeOperator: "Mit deinem Team",
			modeOperatorHint:
				"Arbeitet mit deinem Team: erledigt Aufgaben, erstellt Ansichten und automatisiert mit deinen Berechtigungen.",
			objectiveLabel: "Wofür er da ist",
			objectiveHint:
				"Den Ton, die Grenzen und wann er eine Person holt, stellst du danach unter Diesen Agenten einstellen.",
		},
		/**
		 * O QUE UM AGENTE SABE, em três títulos — lido de
		 * `/v3/agents/:code/capabilities`, pelas mesmas funções que o turno usa,
		 * para o painel nunca prometer o que o turno não oferece.
		 *
		 * `artifacts` é SABE FAZER: as funções do sistema ao alcance dele, com um
		 * interruptor por agente. `sees` é SABE SOBRE: o que lhe põem na mão antes
		 * de responder. `tools` é SEMPRE SABE: as mãos que vêm com o modo, que
		 * ninguém liga aqui — `ToolMatrix`, logo abaixo, é que é o editor.
		 */
		capabilities: {
			title: "Was {name} weiß",
			sees: {
				title: "Kennt",
				lead: "Was ihm in die Hand gegeben wird, bevor er antwortet.",
				businessProfile: "Unternehmensprofil",
				brandRules: "Markenregeln",
				persona: "Persona",
				memories: "{n} Erinnerung | {n} Erinnerungen",
				documents: "{n} Dokument | {n} Dokumente",
				fixBusiness: "unter Konto ausfüllen",
				fixBrand: "unter Konto schreiben",
				fixPersona: "unten beantworten",
				fixDocuments: "unter Gedächtnis hochladen",
			},
			artifacts: {
				title: "Kann tun",
				lead: "Die Funktionen deines Systems. Eine einzuschalten lässt diesen Agenten sie selbst ausführen.",
				empty: "noch keine Funktion im Einsatz",
				inputs: "Bekommt: {list}",
				optional: "{name} (optional)",
				byMode: "weil er {mode} ist",
				byAutomation: "läuft per Automation",
				teamOnly: "nur das Team",
				switchLabel: "{name} {artifact} ausführen lassen",
				error: "Konnte nicht ändern, wer das ausführen darf.",
			},
			tools: {
				title: "Kann immer",
				lead: "Was er von selbst tut, in jedem Gespräch.",
				count: "{n} Werkzeug | {n} Werkzeuge",
			},
			error: "Konnte nicht lesen, was dieser Agent weiß.",
		},
		tools: {
			title: "Werkzeuge",
			count: "({on} von {total})",
			lead: "Nur die Werkzeuge, die der Modus {mode} sehen kann. Eines abzuschalten ist dasselbe, als gäbe es es für diesen Agenten nicht.",
			allOn: "Alle eingeschaltet — es wurde noch nichts eingeschränkt.",
			technical: "Technische Details",
		},
		authority: {
			title: "Autorität",
			hint: "Ein Agent hat eine Rolle wie eine Person. Der Zugriff geht nie über die Rolle hinaus. Was er sieht, wird unter Einstellungen → Zugriffe festgelegt, für Menschen und Agenten gleichermaßen.",
			role: "Läuft als {role}",
			changeIn: "Ändern unter Einstellungen → Zugriffe",
		},
		chat: {
			composePlaceholder: "Nachricht schreiben…",
			placeholder: "Schreib…  Enter sendet, Umschalt+Enter macht einen Zeilenumbruch",
			answering: "antwortet…",
			stop: "Stopp",
			queue: "Anhängen",
			queued: "Danach: {text}",
			attach: "Datei anhängen",
			attachHint:
				"Datei anhängen (Bild, Video, Audio, PDF, Tabelle) — oder einfach einfügen (Strg+V) und ziehen",
			record: "Sprachnotiz aufnehmen",
			stopRecording: "Aufnahme abschließen",
			cancelRecording: "Aufnahme verwerfen",
			voiceNote: "🎙️ Sprachnotiz",
			removeAudio: "Sprachnotiz entfernen",
			noMicrophone: "Auf das Mikrofon konnte nicht zugegriffen werden.",
			removeAttached: "{name} entfernen",
			sentImage: "angehängtes Bild",
			editQuestion: "Frage korrigieren",
			cancelEdit: "abbrechen",
			resend: "erneut senden",
			editAndResend: "bearbeiten und erneut senden",
			silent: "Der Agent hat sich entschieden, diesmal nicht zu antworten.",
			fromAgent: "Ergebnis eines Hintergrund-Agenten",
			refused:
				"Der Modellanbieter hat diese Nachricht abgelehnt. Sie bleibt aus dem Kontext dieses Gesprächs — frag anders.",
			longThread:
				"Lange Konversation — jede Antwort liest alles noch einmal. Eine neue zu beginnen ist billiger und schneller.",
			toEnd: "↓ ans Ende",
			showMore: "Mehr anzeigen",
			showLess: "Weniger anzeigen",
			copy: "kopieren",
			copied: "kopiert",
			useAsReply: "als Antwort verwenden",
			regenerate: "neu erzeugen",
			deleteMessage: "Nachricht löschen",
			confirmDelete: "wirklich löschen?",
			tokens: "· {n} Tokens",
			whatIsThis: "Was ist das?",
		},
		suggestions: {
			hello: "Hallo 👋",
			toStart: "Zum Anfangen",
			orWrite: "Tippen Sie auf einen Vorschlag oder schreiben Sie, was Sie brauchen.",
			orWriteAlone: "Schreiben Sie eine Frage, eine Bitte oder einen Befehl, um zu beginnen.",
			manage: {
				title: "Vorgeschlagene Fragen",
				lead: "Was der Assistent anbietet, wenn er auf einem Bildschirm geöffnet wird. Aus Ihrem Geschäft geschrieben — und von Ihnen änderbar.",
				generate: "Aus meinem Geschäft schreiben",
				generating: "schreibt…",
				generated: "{n} Fragen geschrieben.",
				unavailable: "Kein Modell hat geantwortet. Es wurde nichts geändert.",
				unreadable: "Die Antwort war nicht lesbar. Es wurde nichts geändert.",
				failed: "Konnte sie nicht schreiben: {reason}. Es wurde nichts geändert.",
				anyScreen: "Jeder Bildschirm",
				gone: "Ein Bildschirm, der nicht mehr im Menü steht",
				add: "Neue Frage",
				questionLabel: "Die Frage, in den Worten dessen, der sie stellen würde",
				shortLabel: "Kurzer Text für die Schaltfläche (optional)",
				save: "Speichern",
				cancel: "Abbrechen",
				remove: "Entfernen",
				turnOn: "einschalten",
				turnOff: "ausschalten",
				off: "aus",
				byModel: "vom Modell geschrieben",
				empty:
					"Noch nichts geschrieben. Lassen Sie sie aus Ihrem Geschäft schreiben oder schreiben Sie die erste.",
				/** As de fábrica — ver o bloco pt-BR para o porquê do verbo. */
				factoryHint:
					"Das sind die ab Werk: was Ben auf jedem Bildschirm anbietet, solange dieser Workspace keine geschrieben hat.",
				adopt: "Eine eigene Kopie behalten",
				screenPick: "Bildschirm",
			},
			hintInConversation:
				"Frag ihn, was du antworten sollst. Er liest das Gedächtnis des Workspace und spricht nicht mit dem Kunden.",
			hintInternal:
				"Bitte Ben, Aufgaben zu erledigen, Ansichten zu erstellen und Arbeit zu automatisieren.",
			panel: {
				/**
				 * A PRIMEIRA das três, por decisão do dono (24/09): o que um workspace
				 * recém-nascido quer do Ben é o site no ar, não um resumo do dia que
				 * ainda não teve nada dentro.
				 */
				site: "Erstelle die Website meines Unternehmens",
				/**
				 * UM GESTO NATIVO (desde 05/10 o atendente etiqueta a pessoa da
				 * conversa sem pacote nenhum, e o Ben do painel sempre etiquetou).
				 *
				 * Escrita da boca de QUEM PEDE, como todas as outras: o que se toca
				 * aqui é um pedido ao Ben, não uma frase dele.
				 */
				tagging: "Markiere die Gespräche für mich",
				/** Os atalhos do "/" no compositor: a tela com papel `composer.snippet`. */
				snippets: "Lege die Schnellantworten für den Support an",
				screen: "Leg eine Maske für Angebote an",
				/**
				 * A pergunta de quem JÁ TEM MOVIMENTO — e a última da oferta, por
				 * isso: numa casa recém-aberta não há registro para estar parado.
				 */
				stalled: "Welche Datensätze liegen seit über einer Woche still?",
				/**
				 * NÃO está na oferta, e é usada como CONTRA-EXEMPLO: ela é a pergunta
				 * certa no painel e a errada com um cliente à espera (`ben-dock.spec`).
				 */
				today: "Was hat sich heute geändert?",
			},
			attendant: {
				discount: "Wie würdest du auf eine Rabattanfrage antworten?",
			},
			inConversation: {
				reply: "Schlag eine Antwort vor",
				summarise: "Fasse diese Konversation in drei Zeilen zusammen",
				known: "Was wissen wir über diesen Kunden schon?",
			},
		},
		missionLive: {
			main: "Ben",
			thinking: "Denkt nach",
			reading: "Liest {site}",
			readingPage: "Liest eine Seite",
			/** `change_system` numa missão é sempre um PLANO: ninguém ao volante aplica (QA-007). */
			drafting: "Vorschlag wird entworfen",
			crew: {
				title: "{n} Teile des Vorschlags werden gleichzeitig entworfen",
				done: "{n} fertig",
				running: "{n} in Arbeit",
				queued: "{n} wartend",
				chars: "{kb} KB geschrieben",
				planning: "plant",
			},
			status: {
				queued: "wartet",
				running: "schreibt",
				succeeded: "fertig",
				failed: "fehlgeschlagen",
			},
		},
		live: {
			acting: "handelt…",
			writing: "schreibt {tool} — {kb} KB…",
			writingLive: "live",
			activity: "Aktivität",
			reasoningRaw: "Gedankengang (dev)",
			tasks: "{n} Schritte, {running} laufen",
			credits: "{n} cr",
			expand: "öffnen",
			collapse: "schließen",
			awaitsHuman: "Wartet auf die Antwort einer Person.",
			running: "läuft",
			thinking: "denkt nach…",
			reasoning: "Gedankengang",
			closeOutput: "schließen",
			seeOutput: "Ausgabe ansehen",
		},
		steps: {
			one: "Werkzeug",
			many: "Werkzeuge",
			input: "Eingabe",
			output: "Ausgabe",
			running: "läuft…",
			fields: "{n} Felder",
		},
		approval: {
			approve: "Freigeben",
			done: "Freigegeben — die Agenten nutzen es bereits.",
			discarded: "Dieser Vorschlag wurde verworfen.",
			failed:
				"Freigabe nicht möglich. Versuch es erneut oder nutze Gedächtnis → Was das Team beigebracht hat.",
			replaces: "ersetzt die aktuelle Zeile",
			toLibrary: "kommt in die Bibliothek",
			forAgent: "nur für {agent}",
			title: {
				generic: "Wartet auf deine Freigabe",
				knowledge: "Wissen für die Agenten",
			},
		},
		navigate: {
			contact: "Kontakt",
			conversation: "Konversation",
			record: "Datensatz",
			screen: "Maske",
			back: "zurück",
			open: "öffnen",
			toolFile: "Datei, die das Werkzeug zurückgegeben hat",
		},
		sessions: {
			searchFailed: "Suche fehlgeschlagen. Bitte erneut versuchen.",
			filter: "Konversationen filtern",
			empty: "Noch keine Konversation.",
			noMatch: "Nichts zu „{term}“.",
			aboutConversation: "Über eine Konversation",
			conversationBadge: "Konversation",
			untitled: "Ohne Titel",
			messages: "{n} Nachrichten",
			removeLabel: "{title} löschen",
			thisConversation: "diese Konversation",
			confirmRemove: "bestätigen",
			remove: "löschen",
			/** O cabeçalho do painel de conversas quando ele ocupa a doca inteira. */
			heading: "Frühere Konversationen",
			/** Os grupos por data, no fuso do workspace. */
			groups: {
				today: "Heute",
				yesterday: "Gestern",
				week: "Letzte 7 Tage",
				older: "Älter",
			},
			ago: {
				now: "jetzt",
				minutes: "{n} Min.",
				hours: "{n} Std.",
				days: "{n} T.",
			},
		},
		dock: {
			purposeInConversation: "nur du siehst das — von hier geht nichts raus",
			confirmRestart: "Neu anfangen? Nochmal klicken",
			noBen:
				"In diesem Workspace ist kein Panel-Assistent eingeschaltet. Schalte unter Agenten einen ein, um hier drin um Hilfe zu bitten.",
			purposeInternal: "arbeiten, erstellen und automatisieren",
			acting: "benutzt ein Werkzeug…",
			typing: "schreibt…",
			thinking: "denkt nach…",
			whichAgent: "Mit welchem Agenten sprechen",
			conversations: "Konversationen",
			newConversation: "Neue Konversation",
			wide: "Vollbild",
			narrow: "zurück an die Seite",
			close: "Schließen",
			/** O botão de histórico com o painel aberto: o clique seguinte devolve o chat. */
			backToChat: "Zurück zur Konversation",
			hideHistory: "Gespräche ausblenden",
			copilot: "Ben · Copilot",
			launcher: "Chat mit {name}",
			resize: "Ziehen, um die Breite der Unterhaltung anzupassen",
			tipTitle: "{name} ist da",
			tipBody:
				"Tippe oder klicke auf die hervorgehobene Kugel in der oberen Leiste, um Ben zu öffnen. Erneutes Klicken schließt den Chat. Dein Gespräch bleibt erhalten.",
			tipShortcut:
				"Mit Tastatur: Ctrl + J (Windows/Linux) oder ⌘ + J (Mac) öffnet oder schließt Ben, wenn du nicht in einem Feld schreibst.",
			tipDone: "Genau! Wenn Sie Hilfe brauchen, einfach klicken.",
			tipDismiss: "Verstanden",
		},
		attach: {
			file: "Datei",
			openFile: "{name} öffnen",
			open: "öffnen",
			download: "herunterladen",
			noPort: "Dieser Link öffnet keine Dateien.",
			downloadFailed: "Diese Datei konnte nicht heruntergeladen werden.",
			openFailed: "Diese Datei konnte nicht geöffnet werden.",
			badType: "{name}: Ben liest {formats}.",
			tooBig: "{name}: Datei zu groß (höchstens {max} MB).",
			uploadFailed: "{name}: Das Hochladen ist fehlgeschlagen — bitte erneut versuchen.",
			tooMany: "Bis zu {max} Dateien pro Nachricht.",
			uploading: "wird hochgeladen…",
			unreadable: "das Bild konnte nicht gelesen werden",
		},
		rating: {
			up: "die Antwort hat geholfen",
			down: "die Antwort hat nicht geholfen",
			seeTurn: "den Durchgang ansehen",
			whatWasWrong: "was war falsch? (optional)",
			whatWasWrongLabel: "Was war falsch",
			send: "senden",
		},
		recovery: {
			checking: "Die Verbindung ist abgebrochen — es wird geprüft, ob die Antwort angekommen ist…",
			arrived: "Die Verbindung ist mittendrin abgebrochen, aber die Antwort ist angekommen.",
			lost: "Die Verbindung ist abgebrochen. Die Antwort kann trotzdem erzeugt und gespeichert worden sein — sieh dir den Durchgang an, bevor du noch einmal sendest.",
			seeTurn: "den Durchgang ansehen",
		},
		error: {
			expired: "deine Sitzung ist abgelaufen — melde dich neu an",
			couldNotStart: "Start nicht möglich ({status})",
			somethingWentWrong: "etwas ist schiefgegangen",
			forbidden: "Das kann nur ein Administrator.",
			unreachable: "Der Server war nicht erreichbar.",
			generic: "Es konnte nicht abgeschlossen werden.",
		},
		...agentToolMessages.de,
	},
} as const;
